import { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { DataTable } from "./DataTable";
import { ReviewModal } from "./ReviewModal";
import UserContext from "../../context/user";
import Results from "./Results";
import Search from "@mui/icons-material/Search";
import Business from "@mui/icons-material/Business";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import BackButton from "../bulk-operations/BackButton";
import { Download as DownloadIcon } from "@mui/icons-material";
import reviewConsoleOverviewImg from "../../assets/userguide/reviewconsole/ScreenshotReviewConsoleoverview.png";
import ephTabImg from "../../assets/userguide/reviewconsole/EPHTab.png";
import vendorTabImg from "../../assets/userguide/reviewconsole/VendorTab.png";
import notFoundTabImg from "../../assets/userguide/reviewconsole/NotFoundTab.png";
import headerCheckboxAutoSelectedImg from "../../assets/userguide/reviewconsole/HeaderCheckboxforAutoSelected.png";
import rightSidePanelDetailImg from "../../assets/userguide/reviewconsole/RightSidePanelDetail.png";
import onDemandVendorSearchImg from "../../assets/userguide/reviewconsole/OnDemandVendorSearch.png";
import clearSelectionImg from "../../assets/userguide/reviewconsole/ClearSelection.png";
import reviewSelectionSubmitImg from "../../assets/userguide/reviewconsole/ReviewSelectionSubmit.png";
import Api from "../../service/api";
import "./bulk-upload.css";

const SOURCE_ALIASES = {
  eph: "eph",
  vendor: "vendor",
};

const ENTITY_CONFIG = {
  hcp: { sourceField: "hcp_source", resultKey: "hcp" },
  hco: { sourceField: "hco_source", resultKey: "hco" },
  parentHco: { sourceField: "parent_hco_source", resultKey: "parent_hco" },
};

const BULK_FETCH_PAGE_SIZE = 9;
const BULK_FETCH_BATCH_PAGES = 5;
const BULK_FETCH_BATCH_THRESHOLD =
  BULK_FETCH_PAGE_SIZE * BULK_FETCH_BATCH_PAGES + 4;
const STATUS_API_POLL_INTERVAL_MS = 15000;
const DEFAULT_SUMMARY_BREAKDOWN = {
  ephTotal: 0,
  ephCompleted: 0,
  vendorTotal: 0,
  vendorCompleted: 0,
  notFoundTotal: 0,
  notFoundCompleted: 0,
};

const RESULT_FIELD_CONFIG = {
  eph: {
    id: ["EntityId"],
    enterpriseId: [
      "HCPUniqueId",
      "HCOUniqueId",
      "HCPReferenceId",
      "HCOReferenceId",
      "EntityId",
    ],
    name: ["Name"],
    country: ["CountryCode"],
    classificationCode: ["ClassificationCode"],
    primarySpecialty: ["Specialities"],
    firstName: ["FirstName"],
    lastName: ["LastName"],
    type: ["type"],
  },
  vendor: {
    id: ["onekeyEid", "entityId", "entity.vid__v", "entity.veevaid__v"],
    enterpriseId: [
      "onekeyEid",
      "entityId",
      "activity.activityEid",
      "individual.individualEid",
      "entity.vid__v",
      "entity.veevaid__v",
    ],
    name: [
      "workplace.usualName",
      "individual.fullName",
      "individual.displayName",
      "entity.full_name__v",
      "entity.formatted_name__v",
      "entity.corporate_name__v",
      "entity.hco_name_cda__v",
      "entity.name",
    ],
    country: [
      "country",
      "entity.primary_country__v",
      "entity.country_cda__v",
      "entity.country__v",
      "workplace.workplaceAddresses.P,1.address.postalTownReference.country",
    ],
    classificationCode: [
      "entity.hcp_type__v",
      "entity.hco_type__v",
      "individual.typeLabel",
      "entityType",
      "entity.entityType",
      "entity.hcp_type_local__v",
      "entity.hco_type_local__v",
    ],
    primarySpecialty: [
      "entity.specialty_1__v",
      "activity.typeLabel",
      "entity.spec_1_local__v",
      "entity.spec_2_local__v",
    ],
    firstName: [
      "individual.firstName",
      "entity.first_name__v",
      "entity.first_name",
    ],
    lastName: [
      "individual.lastName",
      "entity.last_name__v",
      "entity.last_name",
    ],
    type: [
      "entityType",
      "entity.entityType",
      "entity.hco_type_local__v",
      "entity.hcp_type_local__v",
    ],
  },
};

const readByPath = (obj, path) => {
  if (!obj || !path) {
    return undefined;
  }
  return path
    .split(".")
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

const firstDefinedValue = (obj, paths) => {
  for (const path of paths || []) {
    const value = readByPath(obj, path);
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
};

const getMappedField = (
  item,
  sourceType,
  fieldName,
  fallbackValue = undefined,
) => {
  const config = RESULT_FIELD_CONFIG[sourceType] || RESULT_FIELD_CONFIG.eph;
  const configuredValue = firstDefinedValue(item, config[fieldName]);

  if (configuredValue !== undefined) {
    return configuredValue;
  }

  if (sourceType === "vendor") {
    const vendorEntity =
      item?.entity && typeof item.entity === "object" ? item.entity : null;
    const entityConfiguredValue = firstDefinedValue(
      vendorEntity,
      config[fieldName],
    );

    if (entityConfiguredValue !== undefined) {
      return entityConfiguredValue;
    }

    if (fieldName === "name") {
      const derivedName =
        `${item?.individual?.firstName || vendorEntity?.first_name__v || ""} ${item?.individual?.lastName || vendorEntity?.last_name__v || ""}`.trim();
      if (derivedName) {
        return derivedName;
      }
      return (
        item?.activity?.activityEid ||
        item?.onekeyEid ||
        item?.entityId ||
        vendorEntity?.vid__v ||
        vendorEntity?.veevaid__v ||
        fallbackValue
      );
    }
    if (fieldName === "country") {
      return (
        item?.country ||
        vendorEntity?.primary_country__v ||
        vendorEntity?.country_cda__v ||
        vendorEntity?.country__v ||
        fallbackValue
      );
    }
    if (fieldName === "primarySpecialty") {
      return (
        vendorEntity?.specialty_1__v ||
        item?.individual?.typeLabel ||
        vendorEntity?.spec_1_local__v ||
        vendorEntity?.spec_2_local__v ||
        item?.activity?.statusLabel ||
        fallbackValue
      );
    }
    if (fieldName === "classificationCode") {
      return (
        vendorEntity?.hcp_type__v ||
        vendorEntity?.hco_type__v ||
        item?.individual?.typeLabel ||
        item?.entityType ||
        vendorEntity?.entityType ||
        vendorEntity?.hcp_type_local__v ||
        vendorEntity?.hco_type_local__v ||
        fallbackValue
      );
    }
    if (fieldName === "type") {
      return (
        item?.entityType ||
        vendorEntity?.entityType ||
        vendorEntity?.hco_type_local__v ||
        vendorEntity?.hcp_type_local__v ||
        fallbackValue
      );
    }
  }

  return fallbackValue;
};

const getVendorAddress = (item) => {
  const vendorEntity =
    item?.entity && typeof item.entity === "object" ? item.entity : null;
  const workplaceAddresses = item?.workplace?.workplaceAddresses;
  if (!workplaceAddresses || typeof workplaceAddresses !== "object") {
    const entityAddresses = vendorEntity?.addresses__v;
    if (!Array.isArray(entityAddresses) || entityAddresses.length === 0) {
      return (
        vendorEntity?.formatted_address__v ||
        vendorEntity?.street_address_1__v ||
        ""
      );
    }

    const selectedAddress =
      entityAddresses.find(
        (address) => Number(address?.address_ordinal__v) === 1,
      ) ||
      entityAddresses.find(
        (address) =>
          String(address?.primary_cda__v || "").toUpperCase() === "Y",
      ) ||
      entityAddresses[0] ||
      {};

    return (
      selectedAddress?.formatted_address__v ||
      selectedAddress?.address_line_1__v ||
      selectedAddress?.street_address_1__v ||
      selectedAddress?.delivery_address__v ||
      ""
    );
  }

  const addresses = Object.values(workplaceAddresses);
  const selected =
    addresses.find((entry) => Number(entry?.rank) === 1) || addresses[0] || {};
  const longLabel = selected?.address?.addressLongLabel || "";
  const village = selected?.address?.postalTownReference?.villageLabel || "";

  return `${longLabel} ${village}`.trim();
};

const normalizeVendorSearchStatus = (entry) => {
  const statusSource =
    entry?.vendorSearchStatus ||
    entry?.vendor_search_status ||
    entry?.vendorSearch ||
    entry?.vendor_search ||
    {};

  const normalized = {
    opted_for_hcp_vendor_search: !!(
      statusSource?.opted_for_hcp_vendor_search ??
      entry?.opted_for_hcp_vendor_search
    ),
    hcp_vendor_search_status:
      statusSource?.hcp_vendor_search_status ??
      entry?.hcp_vendor_search_status ??
      "",
    opted_for_hco_vendor_search: !!(
      statusSource?.opted_for_hco_vendor_search ??
      entry?.opted_for_hco_vendor_search
    ),
    hco_vendor_search_status:
      statusSource?.hco_vendor_search_status ??
      entry?.hco_vendor_search_status ??
      "",
    opted_for_parent_hco_vendor_search: !!(
      statusSource?.opted_for_parent_hco_vendor_search ??
      entry?.opted_for_parent_hco_vendor_search
    ),
    parent_hco_vendor_search_status:
      statusSource?.parent_hco_vendor_search_status ??
      entry?.parent_hco_vendor_search_status ??
      "",
  };

  const hasVendorStatus =
    normalized.hcp_vendor_search_status ||
    normalized.hco_vendor_search_status ||
    normalized.parent_hco_vendor_search_status;

  const requestedTypes = [
    normalized.opted_for_hcp_vendor_search ? "hcp" : null,
    normalized.opted_for_hco_vendor_search ? "hco" : null,
    normalized.opted_for_parent_hco_vendor_search ? "parent_hco" : null,
  ].filter(Boolean);

  return {
    ...normalized,
    requestedTypes,
    hasVendorStatus: !!hasVendorStatus || requestedTypes.length > 0,
    vendorStatusView:
      !!hasVendorStatus || requestedTypes.length > 0 ? "vendor" : "eph",
    vendorSearchRequested: !!hasVendorStatus || requestedTypes.length > 0,
    vendorSearchInProgress: [
      normalized.hcp_vendor_search_status,
      normalized.hco_vendor_search_status,
      normalized.parent_hco_vendor_search_status,
    ].some(
      (value) => String(value || "").toUpperCase() === "SUBMITTED_IN_QUEUE",
    ),
  };
};

const mapApiResultMatches = (
  matches,
  fallbackStatus,
  idPrefix,
  sourceType,
  fallbackName = "",
) => {
  if (!Array.isArray(matches)) {
    return [];
  }

  const normalizedSource = String(sourceType || "")
    .trim()
    .toLowerCase();
  const resolvedStatus =
    normalizedSource === "vendor"
      ? "Found in Vendor"
      : normalizedSource === "eph"
        ? "Found in EPH"
        : fallbackStatus;

  return matches.map((item, index) => ({
    id: getMappedField(item, sourceType, "id") || `${idPrefix}-${index + 1}`,
    enterpriseId: getMappedField(item, sourceType, "enterpriseId") || "N/A",
    name:
      getMappedField(item, sourceType, "name", fallbackName) ||
      fallbackName ||
      "N/A",
    country: getMappedField(item, sourceType, "country") || "N/A",
    status: item.Source || item.source || resolvedStatus,
    classificationCode:
      getMappedField(item, sourceType, "classificationCode") || "N/A",
    primarySpecialty:
      getMappedField(item, sourceType, "primarySpecialty") || "N/A",
    firstName: getMappedField(item, sourceType, "firstName") || "",
    lastName: getMappedField(item, sourceType, "lastName") || "",
    type: getMappedField(item, sourceType, "type") || "",
    sourceType: normalizedSource || sourceType || "",
    address:
      (sourceType === "vendor" ? getVendorAddress(item) : item?.Address) || "",
    backendPayload: item,
  }));
};

const getEntityMatchesFromSearchResults = (
  searchResults,
  sourceKey,
  resultKey,
) => {
  const sourceBucket = searchResults?.[sourceKey] || {};
  const rawValue =
    sourceBucket?.[resultKey] ||
    sourceBucket?.[resultKey?.toUpperCase?.()] ||
    sourceBucket?.[resultKey?.toLowerCase?.()] ||
    [];

  if (Array.isArray(rawValue)) {
    return rawValue;
  }

  if (!rawValue || typeof rawValue !== "object") {
    return [];
  }

  if (Array.isArray(rawValue.matches)) {
    return rawValue.matches;
  }
  if (Array.isArray(rawValue.results)) {
    return rawValue.results;
  }
  if (Array.isArray(rawValue.data)) {
    return rawValue.data;
  }
  if (Array.isArray(rawValue.records)) {
    return rawValue.records;
  }

  return [rawValue];
};

const mapApiResponseToRecords = (payload, startIndex = 0) => {
  const records = Array.isArray(payload) ? payload : [];
  const eph = [];
  const vendor = [];
  const notFound = [];

  const normalizeApiSourceValue = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const normalizeSource = (value) => {
    const source = normalizeApiSourceValue(value);
    return SOURCE_ALIASES[source] || "";
  };

  const resolveEntitySource = (entry, entityName, rowSource) => {
    const sourceField = ENTITY_CONFIG[entityName]?.sourceField;
    return normalizeSource(entry?.[sourceField]) || rowSource;
  };

  const buildRecord = (entry, index, idPrefix, statusLabel, sourceKey) => {
    const rowNumber = startIndex + index + 1;
    const hcpInput = entry?.input?.hcp || entry?.input?.HCP || {};
    const hcoInput = entry?.input?.hco || entry?.input?.HCO || {};
    const parentHcoInput =
      entry?.input?.parent_hco || entry?.input?.Parent_HCO || null;
    const inputPayload = Object.keys(hcpInput).length > 0 ? hcpInput : hcoInput;
    const hcpSource = resolveEntitySource(entry, "hcp", sourceKey);
    const hcoSource = resolveEntitySource(entry, "hco", sourceKey);
    const parentHcoSource = resolveEntitySource(entry, "parentHco", sourceKey);

    const hcpFallbackName =
      `${hcpInput.first_name || ""} ${hcpInput.last_name || ""}`.trim() ||
      hcpInput.name ||
      "";
    const hcoFallbackName = hcoInput.name || "";
    const parentHcoFallbackName = parentHcoInput?.rel_hco_name || "";

    const hcpMatches = mapApiResultMatches(
      getEntityMatchesFromSearchResults(
        entry?.search_results,
        hcpSource,
        ENTITY_CONFIG.hcp.resultKey,
      ),
      statusLabel,
      `${idPrefix}-${index + 1}-hcp`,
      hcpSource,
      hcpFallbackName,
    );
    const hcoMatches = mapApiResultMatches(
      getEntityMatchesFromSearchResults(
        entry?.search_results,
        hcoSource,
        ENTITY_CONFIG.hco.resultKey,
      ),
      statusLabel,
      `${idPrefix}-${index + 1}-hco`,
      hcoSource,
      hcoFallbackName,
    );
    const parentHcoMatches = mapApiResultMatches(
      getEntityMatchesFromSearchResults(
        entry?.search_results,
        parentHcoSource,
        ENTITY_CONFIG.parentHco.resultKey,
      ),
      statusLabel,
      `${idPrefix}-${index + 1}-parent-hco`,
      parentHcoSource,
      parentHcoFallbackName,
    );

    const ephAccordionData = {
      hcp: mapApiResultMatches(
        getEntityMatchesFromSearchResults(
          entry?.search_results,
          "eph",
          ENTITY_CONFIG.hcp.resultKey,
        ),
        "Found in EPH",
        `${idPrefix}-${index + 1}-hcp-eph`,
        "eph",
        hcpFallbackName,
      ),
      hco: mapApiResultMatches(
        getEntityMatchesFromSearchResults(
          entry?.search_results,
          "eph",
          ENTITY_CONFIG.hco.resultKey,
        ),
        "Found in EPH",
        `${idPrefix}-${index + 1}-hco-eph`,
        "eph",
        hcoFallbackName,
      ),
      parentHco: mapApiResultMatches(
        getEntityMatchesFromSearchResults(
          entry?.search_results,
          "eph",
          ENTITY_CONFIG.parentHco.resultKey,
        ),
        "Found in EPH",
        `${idPrefix}-${index + 1}-parent-hco-eph`,
        "eph",
        parentHcoFallbackName,
      ),
    };

    const vendorAccordionData = {
      hcp: mapApiResultMatches(
        getEntityMatchesFromSearchResults(
          entry?.search_results,
          "vendor",
          ENTITY_CONFIG.hcp.resultKey,
        ),
        "Found in Vendor",
        `${idPrefix}-${index + 1}-hcp-vendor`,
        "vendor",
        hcpFallbackName,
      ),
      hco: mapApiResultMatches(
        getEntityMatchesFromSearchResults(
          entry?.search_results,
          "vendor",
          ENTITY_CONFIG.hco.resultKey,
        ),
        "Found in Vendor",
        `${idPrefix}-${index + 1}-hco-vendor`,
        "vendor",
        hcoFallbackName,
      ),
      parentHco: mapApiResultMatches(
        getEntityMatchesFromSearchResults(
          entry?.search_results,
          "vendor",
          ENTITY_CONFIG.parentHco.resultKey,
        ),
        "Found in Vendor",
        `${idPrefix}-${index + 1}-parent-hco-vendor`,
        "vendor",
        parentHcoFallbackName,
      ),
    };

    const topMatch = hcpMatches[0] || hcoMatches[0] || parentHcoMatches[0];
    const defaultName =
      inputPayload.name ||
      `${inputPayload.first_name || ""} ${inputPayload.last_name || ""}`.trim() ||
      "Unknown Record";
    const defaultCountry =
      inputPayload.country_code ||
      parentHcoInput?.rel_hco_country_code ||
      "N/A";

    const vendorSearchState = normalizeVendorSearchStatus(entry);
    const baseAccordionData = {
      hcp: hcpMatches,
      hco: hcoMatches,
      parentHco: parentHcoMatches,
    };

    return {
      id: `${idPrefix}-${rowNumber}`,
      pk: entry?.pk || entry?.Pk || entry?.PK || entry?.id || null,
      enterpriseId:
        topMatch?.enterpriseId ||
        inputPayload.enterprise_id ||
        `${idPrefix.toUpperCase()}-${rowNumber}`,
      name: topMatch?.name || defaultName,
      country: topMatch?.country || defaultCountry,
      status: statusLabel,
      hcpSource: normalizeApiSourceValue(entry?.hcp_source),
      hcoSource: normalizeApiSourceValue(entry?.hco_source),
      parentHcoSource: normalizeApiSourceValue(entry?.parent_hco_source),
      hcp_vendor: entry?.hcp_vendor || "",
      hco_vendor: entry?.hco_vendor || "",
      parent_hco_vendor: entry?.parent_hco_vendor || "",
      details: {
        firstName: inputPayload.first_name || topMatch?.firstName || "N/A",
        lastName: inputPayload.last_name || topMatch?.lastName || "N/A",
        identifierType: inputPayload.identifier_type || "N/A",
        identifierId: inputPayload.identifier_id || "N/A",
      },
      hasAccordion:
        hcpMatches.length > 0 ||
        hcoMatches.length > 0 ||
        parentHcoMatches.length > 0,
      consent: true,
      operationType: "CREATE",
      entityType: inputPayload.entity_type || topMatch?.type || "N/A",
      inputEntities: {
        hcp: entry?.input?.hcp || entry?.input?.HCP || null,
        hco: entry?.input?.hco || entry?.input?.HCO || null,
        parentHco: entry?.input?.parent_hco || entry?.input?.Parent_HCO || null,
      },
      accordionData: baseAccordionData,
      ephAccordionData: ephAccordionData,
      vendorAccordionData: vendorAccordionData,
      vendorSearchRequested: vendorSearchState.vendorSearchRequested,
      vendorSearchInProgress: vendorSearchState.vendorSearchInProgress,
      vendorStatusView: vendorSearchState.vendorStatusView,
      vendorSearchRequestTypes: vendorSearchState.requestedTypes,
      vendorSearchStatus: {
        opted_for_hcp_vendor_search:
          vendorSearchState.opted_for_hcp_vendor_search,
        hcp_vendor_search_status: vendorSearchState.hcp_vendor_search_status,
        opted_for_hco_vendor_search:
          vendorSearchState.opted_for_hco_vendor_search,
        hco_vendor_search_status: vendorSearchState.hco_vendor_search_status,
        opted_for_parent_hco_vendor_search:
          vendorSearchState.opted_for_parent_hco_vendor_search,
        parent_hco_vendor_search_status:
          vendorSearchState.parent_hco_vendor_search_status,
      },
    };
  };

  records.forEach((entry, index) => {
    const rowNumber = startIndex + index + 1;
    const normalizedSource = normalizeSource(entry?.source);
    const count = Number(entry?.count || 0);

    // Rule 3: count 0 always routes to Not Found tab.
    if (count === 0 || normalizedSource === "notfound") {
      const hcpInput = entry?.input?.hcp || entry?.input?.HCP || {};
      const hcoInput = entry?.input?.hco || entry?.input?.HCO || {};
      const parentHcoInput =
        entry?.input?.parent_hco || entry?.input?.Parent_HCO || null;
      const inputPayload =
        Object.keys(hcpInput).length > 0 ? hcpInput : hcoInput;
      const defaultName =
        inputPayload.name ||
        `${inputPayload.first_name || ""} ${inputPayload.last_name || ""}`.trim() ||
        "Unknown Record";
      const defaultCountry =
        inputPayload.country_code ||
        parentHcoInput?.rel_hco_country_code ||
        "N/A";

      const vendorSearchState = normalizeVendorSearchStatus(entry);

      notFound.push({
        id: `nf-${rowNumber}`,
        pk: entry?.pk || entry?.Pk || entry?.PK || entry?.id || null,
        enterpriseId: inputPayload.enterprise_id || `UNK-${rowNumber}`,
        name: defaultName,
        country: defaultCountry,
        status: "Not Found",
        hcpSource: normalizeApiSourceValue(entry?.hcp_source),
        hcoSource: normalizeApiSourceValue(entry?.hco_source),
        parentHcoSource: normalizeApiSourceValue(entry?.parent_hco_source),
        hcp_vendor: entry?.hcp_vendor || "",
        hco_vendor: entry?.hco_vendor || "",
        parent_hco_vendor: entry?.parent_hco_vendor || "",
        details: {
          firstName: inputPayload.first_name || "N/A",
          lastName: inputPayload.last_name || "N/A",
          identifierType: inputPayload.identifier_type || "N/A",
          identifierId: inputPayload.identifier_id || "N/A",
        },
        hasAccordion: false,
        operationType: "CREATE",
        entityType: inputPayload.entity_type || "N/A",
        consent: true,
        inputEntities: {
          hcp: entry?.input?.hcp || entry?.input?.HCP || null,
          hco: entry?.input?.hco || entry?.input?.HCO || null,
          parentHco:
            entry?.input?.parent_hco || entry?.input?.Parent_HCO || null,
        },
        accordionData: {
          hcp: [],
          hco: [],
          parentHco: [],
        },
        ephAccordionData: {
          hcp: [],
          hco: [],
          parentHco: [],
        },
        vendorAccordionData: {
          hcp: [],
          hco: [],
          parentHco: [],
        },
        vendorSearchRequested: vendorSearchState.vendorSearchRequested,
        vendorSearchInProgress: vendorSearchState.vendorSearchInProgress,
        vendorStatusView: vendorSearchState.vendorStatusView,
        vendorSearchRequestTypes: vendorSearchState.requestedTypes,
        vendorSearchStatus: {
          opted_for_hcp_vendor_search:
            vendorSearchState.opted_for_hcp_vendor_search,
          hcp_vendor_search_status: vendorSearchState.hcp_vendor_search_status,
          opted_for_hco_vendor_search:
            vendorSearchState.opted_for_hco_vendor_search,
          hco_vendor_search_status: vendorSearchState.hco_vendor_search_status,
          opted_for_parent_hco_vendor_search:
            vendorSearchState.opted_for_parent_hco_vendor_search,
          parent_hco_vendor_search_status:
            vendorSearchState.parent_hco_vendor_search_status,
        },
      });
      return;
    }

    // Rule 1: source EPH routes to EPH tab.
    if (normalizedSource === "eph") {
      eph.push(buildRecord(entry, index, "eph", "Found in EPH", "eph"));
      return;
    }

    // Rule 2: source Vendor routes to Vendor tab.
    if (normalizedSource === "vendor") {
      vendor.push(
        buildRecord(entry, index, "ven", "Found in Vendor", "vendor"),
      );
      return;
    }

    // Safety fallback for unexpected source values.
    notFound.push(buildRecord(entry, index, "nf", "Not Found", "eph"));
  });

  return { eph, vendor, notFound };
};

const LEFT_ENTITY_SEARCH_FIELDS = {
  hcp: [
    "enterprise_id",
    "country_code",
    "first_name",
    "middle_name",
    "last_name",
    "secondary_last_name",
    "prefix",
    "classification_code",
    "active_flag",
    "primary_specialty",
    "specialty_2",
    "specialty_3",
    "phone",
    "email",
    "identifier_1_type",
    "identifier_1_id",
    "identifier_2_type",
    "identifier_2_id",
    "identifier_3_type",
    "identifier_3_id",
    "hcp_adrs_source_adrs_id",
    "hcp_adrs_line_1",
    "hcp_adrs_line_2",
    "hcp_adrs_city",
    "hcp_adrs_state_code",
    "hcp_adrs_country",
    "hcp_adrs_zip",
    "hcp_adrs_rank",
  ],
  hco: [
    "enterprise_id",
    "country_code",
    "name",
    "classification_code",
    "active_flag",
    "primary_specialty",
    "specialty_2",
    "specialty_3",
    "phone",
    "email",
    "source_address_id",
    "hco_adrs_line_1",
    "hco_adrs_city",
    "hco_adrs_state_code",
    "hco_adrs_country",
    "hco_adrs_rank",
    "hco_adrs_zip",
    "identifier_1_type",
    "identifier_1_id",
    "identifier_2_type",
    "identifier_2_id",
    "identifier_3_type",
    "identifier_3_id",
    "rel_hco_enterprise_id",
    "rel_hco_name",
    "rel_hco_clsfctn_cd",
    "rel_hco_country_code",
    "rel_hco_adrs_line_1",
    "rel_hco_adrs_line_2",
    "rel_hco_adrs_city",
    "rel_hco_adrs_state_code",
    "rel_hco_adrs_country",
    "rel_hco_adrs_zip",
  ],
  parentHco: [
    "enterprise_id",
    "country_code",
    "name",
    "classification_code",
    "active_flag",
    "primary_specialty",
    "specialty_2",
    "specialty_3",
    "phone",
    "email",
    "source_address_id",
    "hco_adrs_line_1",
    "hco_adrs_city",
    "hco_adrs_state_code",
    "hco_adrs_country",
    "hco_adrs_rank",
    "hco_adrs_zip",
    "identifier_1_type",
    "identifier_1_id",
    "identifier_2_type",
    "identifier_2_id",
    "identifier_3_type",
    "identifier_3_id",
    "rel_hco_enterprise_id",
    "rel_hco_name",
    "rel_hco_clsfctn_cd",
    "rel_hco_country_code",
    "rel_hco_adrs_line_1",
    "rel_hco_adrs_line_2",
    "rel_hco_adrs_city",
    "rel_hco_adrs_state_code",
    "rel_hco_adrs_country",
    "rel_hco_adrs_zip",
  ],
};

const addSearchValue = (values, value) => {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => addSearchValue(values, item));
    return;
  }

  if (typeof value === "object") {
    Object.values(value).forEach((item) => addSearchValue(values, item));
    return;
  }

  const normalized = String(value).trim();
  if (normalized) {
    values.push(normalized.toLowerCase());
  }
};

const hasEntityInputForSearch = (entity) =>
  !!entity && typeof entity === "object" && Object.keys(entity).length > 0;

const getLeftEntityKeyForRecord = (record) => {
  const inputEntities = record?.inputEntities || {};
  const hasHcp = hasEntityInputForSearch(inputEntities.hcp);
  const hasHco = hasEntityInputForSearch(inputEntities.hco);
  const hasParentHco = hasEntityInputForSearch(inputEntities.parentHco);

  if (hasHcp && !hasHco && !hasParentHco) {
    return "hcp";
  }
  if (hasHco && !hasHcp && !hasParentHco) {
    return "hco";
  }
  if (hasHcp && hasParentHco && !hasHco) {
    return "hcp";
  }
  if (hasHco && hasParentHco && !hasHcp) {
    return "hco";
  }
  if (hasHcp) {
    return "hcp";
  }
  if (hasHco) {
    return "hco";
  }
  return hasParentHco ? "parentHco" : null;
};

const getLeftResultMatchesForRecord = (record) => {
  const inputEntities = record?.inputEntities || {};
  const hasHcp = hasEntityInputForSearch(inputEntities.hcp);
  const hasHco = hasEntityInputForSearch(inputEntities.hco);
  const hasParentHco = hasEntityInputForSearch(inputEntities.parentHco);

  const useHcoOnLeft =
    (hasHco && !hasHcp && !hasParentHco) || (hasHco && hasParentHco && !hasHcp);

  return useHcoOnLeft
    ? record?.accordionData?.hco || []
    : record?.accordionData?.hcp || [];
};

const getRightEntityKeyForRecord = (record) => {
  const inputEntities = record?.inputEntities || {};
  const hasHcp = hasEntityInputForSearch(inputEntities.hcp);
  const hasHco = hasEntityInputForSearch(inputEntities.hco);
  const hasParentHco = hasEntityInputForSearch(inputEntities.parentHco);

  const isSingleHcpLayout = hasHcp && !hasHco && !hasParentHco;
  const isSingleHcoLayout = hasHco && !hasHcp && !hasParentHco;

  if (isSingleHcpLayout || isSingleHcoLayout) {
    return null;
  }

  if (hasParentHco && (hasHcp || hasHco)) {
    return "parentHco";
  }

  if (hasHco && hasHcp) {
    return "hco";
  }

  return null;
};

const getRightResultMatchesForRecord = (record) => {
  const inputEntities = record?.inputEntities || {};
  const hasHcp = hasEntityInputForSearch(inputEntities.hcp);
  const hasHco = hasEntityInputForSearch(inputEntities.hco);
  const hasParentHco = hasEntityInputForSearch(inputEntities.parentHco);

  const isSingleHcpLayout = hasHcp && !hasHco && !hasParentHco;
  const isSingleHcoLayout = hasHco && !hasHcp && !hasParentHco;

  if (isSingleHcpLayout || isSingleHcoLayout) {
    return [];
  }

  const useParentHcoOnRight =
    (hasHcp && hasParentHco && !hasHco) || (hasHco && hasParentHco && !hasHcp);

  return useParentHcoOnRight
    ? record?.accordionData?.parentHco || []
    : record?.accordionData?.hco || [];
};

const tokenizeSearchValue = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();

  if (!normalized) {
    return [];
  }

  return normalized
    .split(/[\s,;:|/\\()[\]{}]+/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const buildLeftSideSearchTokens = (record) => {
  const values = [];

  const leftEntityKey = getLeftEntityKeyForRecord(record);
  if (leftEntityKey) {
    const leftEntity = record?.inputEntities?.[leftEntityKey] || {};
    const configuredFields = LEFT_ENTITY_SEARCH_FIELDS[leftEntityKey] || [];

    configuredFields.forEach((fieldKey) => {
      addSearchValue(values, leftEntity?.[fieldKey]);
    });

    if (configuredFields.length === 0) {
      addSearchValue(values, leftEntity);
    }
  }

  const rightEntityKey = getRightEntityKeyForRecord(record);
  if (rightEntityKey) {
    const rightEntity = record?.inputEntities?.[rightEntityKey] || {};
    const configuredFields = LEFT_ENTITY_SEARCH_FIELDS[rightEntityKey] || [];

    configuredFields.forEach((fieldKey) => {
      addSearchValue(values, rightEntity?.[fieldKey]);
    });

    if (configuredFields.length === 0) {
      addSearchValue(values, rightEntity);
    }
  }

  const uniqueTokens = new Set();
  values.forEach((value) => {
    const normalizedValue = String(value || "")
      .trim()
      .toLowerCase();

    if (!normalizedValue) {
      return;
    }

    // Keep complete field value and tokenized keywords for exact matching.
    uniqueTokens.add(normalizedValue);
    tokenizeSearchValue(normalizedValue).forEach((token) => {
      uniqueTokens.add(token);
    });
  });

  return uniqueTokens;
};

export default function App() {
  const history = useHistory();
  const UserInfo = useContext(UserContext);
  const userCountry = sessionStorage.getItem("userSelectedCountry");
  const uploadId = sessionStorage.getItem("uploadId");
  const [view, setView] = useState("search");
  const [activeTab, setActiveTab] = useState("eph");
  const [ephSelected, setEphSelected] = useState(new Set());
  const [vendorSelected, setVendorSelected] = useState(new Set());
  const [notFoundSelected, setNotFoundSelected] = useState(new Set());
  const [ephLocked, setEphLocked] = useState(false);
  const [vendorLocked, setVendorLocked] = useState(false);
  const [notFoundLocked, setNotFoundLocked] = useState(false);
  const [ephPage, setEphPage] = useState(1);
  const [vendorPage, setVendorPage] = useState(1);
  const [notFoundPage, setNotFoundPage] = useState(1);
  const [ephAccordionSelected, setEphAccordionSelected] = useState({});
  const [vendorAccordionSelected, setVendorAccordionSelected] = useState({});
  const [notFoundAccordionSelected, setNotFoundAccordionSelected] = useState(
    {},
  );
  const [ephRecordsData, setEphRecordsData] = useState([]);
  const [vendorRecordsData, setVendorRecordsData] = useState([]);
  const [notFoundRecordsData, setNotFoundRecordsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [continuePolling, setContinuePolling] = useState(true);
  const [pollingTimeRemaining, setPollingTimeRemaining] = useState(null);
  const [processingProgress, setProcessingProgress] = useState({
    processed: 0,
    total: 0,
  });
  const [summaryBreakdown, setSummaryBreakdown] = useState(
    DEFAULT_SUMMARY_BREAKDOWN,
  );
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showClearSelectionsModal, setShowClearSelectionsModal] =
    useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSkipReviewModal, setShowSkipReviewModal] = useState(false);
  const [selectAllMenuAnchor, setSelectAllMenuAnchor] = useState(null);
  const [headerSelectionMode, setHeaderSelectionMode] = useState({});
  const headerCheckboxRef = useRef(null);
  const [clearSelectionMode, setClearSelectionMode] = useState("reviewed");
  const [submittedEphRecords, setSubmittedEphRecords] = useState([]);
  const [submittedVendorRecords, setSubmittedVendorRecords] = useState([]);
  const [submittedNotFoundRecords, setSubmittedNotFoundRecords] = useState([]);
  const [visitedTabs, setVisitedTabs] = useState(new Set(["eph"]));
  const [keptRecords, setKeptRecords] = useState([]);
  const [ephShowAccordion, setEphShowAccordion] = useState(true);
  const [vendorShowAccordion, setVendorShowAccordion] = useState(true);
  const [notFoundShowAccordion, setNotFoundShowAccordion] = useState(true);
  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [supportedVendorCountries, setSupportedVendorCountries] = useState(
    new Set(),
  );
  const [clearSelectionsRequests, setClearSelectionsRequests] = useState({
    eph: null,
    vendor: null,
    notfound: null,
  });
  const [headerSelectionRequests, setHeaderSelectionRequests] = useState({
    eph: null,
    vendor: null,
    notfound: null,
  });
  const resumePollingTriggeredRef = useRef(new Set());

  const VENDOR_SEARCH_COUNTRY_PREFIXES = [
    "#IQVIA_US",
    "#IQVIA",
    "#Veeva Network",
    "#OPEN-DATA",
  ];

  const fetchAndFilterSupportedCountries = async () => {
    try {
      const response = await Api.getSelectedDCRCountry();
      const allCountries = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : [];
      const supported = new Set();
      allCountries.forEach((country) => {
        if (!country) return;

        const countryName = String(
          country.country_name || country.name || country || "",
        ).trim();
        // Check if country name ends with any of the supported prefixes
        const matchedPrefix = VENDOR_SEARCH_COUNTRY_PREFIXES.find((prefix) =>
          countryName.endsWith(prefix),
        );

        if (matchedPrefix) {
          // Extract base country name by removing the prefix
          const baseCountry = countryName
            .substring(0, countryName.length - matchedPrefix.length)
            .trim();
          supported.add(baseCountry);
        }
      });

      setSupportedVendorCountries(supported);
    } catch (error) {
      setSupportedVendorCountries(new Set());
    }
  };

  const isCountryEligibleForVendorSearch = () => {
    if (!userCountry) {
      return false;
    }

    if (supportedVendorCountries.size === 0) {
      return false;
    }

    const isEligible = supportedVendorCountries.has(userCountry);
    return isEligible;
  };

  useEffect(() => {
    fetchAndFilterSupportedCountries();
  }, []);

  const normalizeCommercialCountries = () => {
    const value =
      UserInfo?.commercialCountry ??
      UserInfo?.commercialCountries ??
      UserInfo?.countries ??
      [];
    return Array.isArray(value) ? value : [value].filter(Boolean);
  };

  const buildVendorPollingSearchType = (requestedTypes = []) => {
    const uniqueTypes = [...new Set(requestedTypes.filter(Boolean))];
    const hasHcp = uniqueTypes.includes("hcp");
    const hasHco = uniqueTypes.includes("hco");
    const hasParentHco = uniqueTypes.includes("parent_hco");

    if (hasHco && hasParentHco && !hasHcp) {
      return "both";
    }

    if (hasHcp && hasParentHco && !hasHco) {
      return "both";
    }

    return uniqueTypes.join(",");
  };

  const getVendorSearchResponseStatus = (response) => {
    const data = response?.data || {};
    const normalized = {
      opted_for_hcp_vendor_search: !!data?.opted_for_hcp_vendor_search,
      hcp_vendor_search_status: data?.hcp_vendor_search_status || "",
      opted_for_hco_vendor_search: !!data?.opted_for_hco_vendor_search,
      hco_vendor_search_status: data?.hco_vendor_search_status || "",
      opted_for_parent_hco_vendor_search:
        !!data?.opted_for_parent_hco_vendor_search,
      parent_hco_vendor_search_status:
        data?.parent_hco_vendor_search_status || "",
    };

    const hasExplicitStatus =
      normalized.hcp_vendor_search_status ||
      normalized.hco_vendor_search_status ||
      normalized.parent_hco_vendor_search_status;

    if (hasExplicitStatus) {
      return normalized;
    }

    return null;
  };

  const patchRecordInCollections = (recordId, updater) => {
    const mapRecords = (records) =>
      records.map((record) =>
        record.id === recordId ? updater(record) : record,
      );

    setEphRecordsData((prev) => mapRecords(prev));
    setVendorRecordsData((prev) => mapRecords(prev));
    setNotFoundRecordsData((prev) => mapRecords(prev));
  };

  const resumeVendorPolling = async (record, requestedTypes) => {
    const recordId = record?.id;
    const pk = record?.pk;

    if (!pk || !recordId || requestedTypes.length === 0) {
      return;
    }

    const triggerKey = `${recordId}:${[...requestedTypes].sort().join(",")}`;
    if (resumePollingTriggeredRef.current.has(triggerKey)) {
      return;
    }
    resumePollingTriggeredRef.current.add(triggerKey);

    patchRecordInCollections(recordId, (prev) => ({
      ...prev,
      vendorSearchInProgress: true,
      vendorStatusView: "vendor",
      vendorAccordionData: {
        hcp: requestedTypes.includes("hcp")
          ? []
          : prev?.vendorAccordionData?.hcp || [],
        hco: requestedTypes.includes("hco")
          ? []
          : prev?.vendorAccordionData?.hco || [],
        parentHco: requestedTypes.includes("parent_hco")
          ? []
          : prev?.vendorAccordionData?.parentHco || [],
      },
    }));

    const pollingSearchType = buildVendorPollingSearchType(requestedTypes);
    let statusPayload = null;
    let shouldContinuePolling = true;
    const maxPollingTime = 50 * 60 * 1000;
    const pollingWaitTime = 5000;
    const startTime = Date.now();

    while (shouldContinuePolling) {
      if (Date.now() - startTime >= maxPollingTime) {
        break;
      }

      await new Promise((resolve) => setTimeout(resolve, pollingWaitTime));

      try {
        const pollingResponse = await Api.fetchVendorSearchPolling({
          upload_id: uploadId,
          pk: pk,
          search_type: pollingSearchType,
        });

        statusPayload =
          getVendorSearchResponseStatus(pollingResponse) || statusPayload;

        const responseData = pollingResponse?.data || {};
        shouldContinuePolling = responseData?.continuePolling === true;

        const searchResultsPayload =
          responseData?.search_results || responseData?.search?.results;
        if (searchResultsPayload) {
          const vendorResults = searchResultsPayload.vendor || {};

          patchRecordInCollections(recordId, (prev) => {
            const updatedVendorAccordionData = {
              hcp: prev?.vendorAccordionData?.hcp || [],
              hco: prev?.vendorAccordionData?.hco || [],
              parentHco: prev?.vendorAccordionData?.parentHco || [],
            };

            const hasHcpVendorSource = Object.prototype.hasOwnProperty.call(
              responseData,
              "hcp_vendor",
            );
            const hasHcoVendorSource = Object.prototype.hasOwnProperty.call(
              responseData,
              "hco_vendor",
            );
            const hasParentHcoVendorSource =
              Object.prototype.hasOwnProperty.call(
                responseData,
                "parent_hco_vendor",
              );

            if (
              requestedTypes.includes("hcp") &&
              Object.prototype.hasOwnProperty.call(vendorResults, "hcp")
            ) {
              updatedVendorAccordionData.hcp = mapApiResultMatches(
                Array.isArray(vendorResults.hcp)
                  ? vendorResults.hcp
                  : vendorResults.hcp.matches ||
                      vendorResults.hcp.results ||
                      [],
                "Found in Vendor",
                `${recordId}-hcp-vendor`,
                "vendor",
                "",
              );
            }
            if (
              requestedTypes.includes("hco") &&
              Object.prototype.hasOwnProperty.call(vendorResults, "hco")
            ) {
              updatedVendorAccordionData.hco = mapApiResultMatches(
                Array.isArray(vendorResults.hco)
                  ? vendorResults.hco
                  : vendorResults.hco.matches ||
                      vendorResults.hco.results ||
                      [],
                "Found in Vendor",
                `${recordId}-hco-vendor`,
                "vendor",
                "",
              );
            }
            if (
              requestedTypes.includes("parent_hco") &&
              Object.prototype.hasOwnProperty.call(vendorResults, "parent_hco")
            ) {
              updatedVendorAccordionData.parentHco = mapApiResultMatches(
                Array.isArray(vendorResults.parent_hco)
                  ? vendorResults.parent_hco
                  : vendorResults.parent_hco.matches ||
                      vendorResults.parent_hco.results ||
                      [],
                "Found in Vendor",
                `${recordId}-parent_hco-vendor`,
                "vendor",
                "",
              );
            }

            return {
              ...prev,
              ephAccordionData: prev?.ephAccordionData || prev?.accordionData,
              vendorAccordionData: updatedVendorAccordionData,
              hcp_vendor: hasHcpVendorSource
                ? responseData?.hcp_vendor
                : prev?.hcp_vendor || "",
              hco_vendor: hasHcoVendorSource
                ? responseData?.hco_vendor
                : prev?.hco_vendor || "",
              parent_hco_vendor: hasParentHcoVendorSource
                ? responseData?.parent_hco_vendor
                : prev?.parent_hco_vendor || "",
              vendorSearchStatus: statusPayload,
            };
          });
        }

        if (!shouldContinuePolling) {
          break;
        }
      } catch (pollingError) {
        console.error(
          `Resume vendor polling error for record ${recordId}:`,
          pollingError,
        );
      }
    }

    patchRecordInCollections(recordId, (prev) => ({
      ...prev,
      vendorSearchInProgress: false,
      vendorStatusView: "vendor",
      vendorSearchStatus: statusPayload || prev?.vendorSearchStatus,
    }));
  };

  const handleVendorSearch = async (recordId, selectedSearchTypes = []) => {
    const requestedTypes = Array.isArray(selectedSearchTypes)
      ? selectedSearchTypes.filter(Boolean)
      : [];
    const record =
      ephRecordsData.find((item) => item.id === recordId) ||
      vendorRecordsData.find((item) => item.id === recordId) ||
      notFoundRecordsData.find((item) => item.id === recordId);

    if (!record || requestedTypes.length === 0) {
      return;
    }

    const pk = record?.pk;
    if (!pk) {
      return;
    }

    const queuedStatus = {
      opted_for_hcp_vendor_search: requestedTypes.includes("hcp"),
      hcp_vendor_search_status: requestedTypes.includes("hcp")
        ? "SUBMITTED_IN_QUEUE"
        : "",
      opted_for_hco_vendor_search: requestedTypes.includes("hco"),
      hco_vendor_search_status: requestedTypes.includes("hco")
        ? "SUBMITTED_IN_QUEUE"
        : "",
      opted_for_parent_hco_vendor_search: requestedTypes.includes("parent_hco"),
      parent_hco_vendor_search_status: requestedTypes.includes("parent_hco")
        ? "SUBMITTED_IN_QUEUE"
        : "",
    };

    patchRecordInCollections(recordId, (prev) => ({
      ...prev,
      vendorSearchRequested: true,
      vendorSearchInProgress: true,
      vendorSearchRequestTypes: requestedTypes,
      vendorStatusView: "vendor",
      ephAccordionData: prev?.ephAccordionData ||
        prev?.accordionData || {
          hcp: [],
          hco: [],
          parentHco: [],
        },
      vendorAccordionData: {
        hcp: requestedTypes.includes("hcp")
          ? []
          : prev?.vendorAccordionData?.hcp || [],
        hco: requestedTypes.includes("hco")
          ? []
          : prev?.vendorAccordionData?.hco || [],
        parentHco: requestedTypes.includes("parent_hco")
          ? []
          : prev?.vendorAccordionData?.parentHco || [],
      },
      vendorSearchStatus: queuedStatus,
    }));

    const payload = {
      upload_id: uploadId,
      eConsentCountry: userCountry,
      commercialCountries: normalizeCommercialCountries(),
      pk: pk,
      ...(requestedTypes.includes("hcp") ? { hcp_search_type: "hcp" } : {}),
      ...(requestedTypes.includes("hco") ? { hco_search_type: "hco" } : {}),
      ...(requestedTypes.includes("parent_hco")
        ? { parent_hco_search_type: "parent_hco" }
        : {}),
    };

    try {
      const triggerResponse = await Api.fetchVendorSearch(payload);
      let statusPayload = getVendorSearchResponseStatus(triggerResponse);

      if (triggerResponse?.status === 200) {
        const pollingSearchType = buildVendorPollingSearchType(requestedTypes);

        // Implement continuous polling for vendor search results
        let shouldContinuePolling = true;
        const maxPollingTime = 50 * 60 * 1000; // 50 minutes
        const pollingWaitTime = 5000; // 5 seconds
        const startTime = Date.now();

        while (shouldContinuePolling) {
          // Check if max polling time exceeded
          if (Date.now() - startTime >= maxPollingTime) {
            console.warn(
              `Vendor search polling timeout for record ${recordId}`,
            );
            break;
          }

          // Wait 10 seconds before polling
          await new Promise((resolve) => setTimeout(resolve, pollingWaitTime));

          try {
            const pollingResponse = await Api.fetchVendorSearchPolling({
              upload_id: uploadId,
              pk: pk,
              search_type: pollingSearchType,
            });

            statusPayload =
              getVendorSearchResponseStatus(pollingResponse) || statusPayload;

            // Check if we should continue polling
            const responseData = pollingResponse?.data || {};
            shouldContinuePolling = responseData?.continuePolling === true;

            // Merge vendor results into record's accordionData
            const searchResultsPayload =
              responseData?.search_results || responseData?.search?.results;
            if (searchResultsPayload) {
              const vendorResults = searchResultsPayload.vendor || {};

              patchRecordInCollections(recordId, (prev) => {
                const updatedVendorAccordionData = {
                  hcp: prev?.vendorAccordionData?.hcp || [],
                  hco: prev?.vendorAccordionData?.hco || [],
                  parentHco: prev?.vendorAccordionData?.parentHco || [],
                };
                const hasHcpVendorSource = Object.prototype.hasOwnProperty.call(
                  responseData,
                  "hcp_vendor",
                );
                const hasHcoVendorSource = Object.prototype.hasOwnProperty.call(
                  responseData,
                  "hco_vendor",
                );
                const hasParentHcoVendorSource =
                  Object.prototype.hasOwnProperty.call(
                    responseData,
                    "parent_hco_vendor",
                  );

                // Update HCP data if available
                if (
                  requestedTypes.includes("hcp") &&
                  Object.prototype.hasOwnProperty.call(vendorResults, "hcp")
                ) {
                  const hcpMatches = mapApiResultMatches(
                    Array.isArray(vendorResults.hcp)
                      ? vendorResults.hcp
                      : vendorResults.hcp.matches ||
                          vendorResults.hcp.results ||
                          [],
                    "Found in Vendor",
                    `${recordId}-hcp-vendor`,
                    "vendor",
                    "",
                  );
                  updatedVendorAccordionData.hcp = hcpMatches;
                }

                // Update HCO data if available
                if (
                  requestedTypes.includes("hco") &&
                  Object.prototype.hasOwnProperty.call(vendorResults, "hco")
                ) {
                  const hcoMatches = mapApiResultMatches(
                    Array.isArray(vendorResults.hco)
                      ? vendorResults.hco
                      : vendorResults.hco.matches ||
                          vendorResults.hco.results ||
                          [],
                    "Found in Vendor",
                    `${recordId}-hco-vendor`,
                    "vendor",
                    "",
                  );
                  updatedVendorAccordionData.hco = hcoMatches;
                }

                // Update Parent HCO data if available
                if (
                  requestedTypes.includes("parent_hco") &&
                  Object.prototype.hasOwnProperty.call(
                    vendorResults,
                    "parent_hco",
                  )
                ) {
                  const parentHcoMatches = mapApiResultMatches(
                    Array.isArray(vendorResults.parent_hco)
                      ? vendorResults.parent_hco
                      : vendorResults.parent_hco.matches ||
                          vendorResults.parent_hco.results ||
                          [],
                    "Found in Vendor",
                    `${recordId}-parent_hco-vendor`,
                    "vendor",
                    "",
                  );
                  updatedVendorAccordionData.parentHco = parentHcoMatches;
                }

                return {
                  ...prev,
                  ephAccordionData:
                    prev?.ephAccordionData || prev?.accordionData,
                  vendorAccordionData: updatedVendorAccordionData,
                  hcp_vendor: hasHcpVendorSource
                    ? responseData?.hcp_vendor
                    : prev?.hcp_vendor || "",
                  hco_vendor: hasHcoVendorSource
                    ? responseData?.hco_vendor
                    : prev?.hco_vendor || "",
                  parent_hco_vendor: hasParentHcoVendorSource
                    ? responseData?.parent_hco_vendor
                    : prev?.parent_hco_vendor || "",
                  vendorSearchStatus: statusPayload,
                };
              });
            }

            // If continuePolling is false, stop the loop
            if (!shouldContinuePolling) {
              break;
            }
          } catch (pollingError) {
            console.error(
              `Vendor search polling error for record ${recordId}:`,
              pollingError,
            );
            // Continue polling even on error, up to max time
          }
        }
      }

      patchRecordInCollections(recordId, (prev) => ({
        ...prev,
        vendorSearchRequested: true,
        vendorSearchInProgress: false,
        vendorStatusView: "vendor",
        vendorSearchStatus: statusPayload || queuedStatus,
      }));
    } catch (error) {
      console.error("Vendor search request failed.", error);
      patchRecordInCollections(recordId, (prev) => ({
        ...prev,
        vendorSearchInProgress: false,
      }));
    }
  };

  useEffect(() => {
    if (!uploadId) {
      history.push("/upload-history");
      return;
    }

    let isCancelled = false;
    let fetchedRows = 0;
    let hasReceivedData = false;
    const controller = new AbortController();
    const signal = controller.signal;
    const advanceSearchType = "advance";
    const maxPollingTime = 50 * 60 * 1000;
    const retryWaitTime = 5000;
    const maxRetries = 3;
    const startTime = Date.now();

    const sleep = (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

    const updatePollingTime = () => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, maxPollingTime - elapsed);
      setPollingTimeRemaining(remaining);
      return remaining > 0;
    };

    const fetchSummaryWithRetry = async (retryCount = 0) => {
      try {
        return await Api.fetchBulkSearchSummery({
          upload_id: uploadId,
          searchType: advanceSearchType,
          fetchFor: "BulkUpload",
        });
      } catch (error) {
        if (
          signal.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          throw error;
        }

        if (retryCount >= maxRetries || isCancelled) {
          throw error;
        }

        await sleep(retryWaitTime);
        return fetchSummaryWithRetry(retryCount + 1);
      }
    };

    const fetchWithRetry = async (index, retryCount = 0) => {
      try {
        return await Api.fetchBulkSearchResults(
          {
            upload_id: uploadId,
            session: index === 0 ? "0" : "1",
            searchType: advanceSearchType,
            fetchFor: "BulkUpload",
          },
          signal,
        );
      } catch (error) {
        if (
          signal.aborted ||
          error?.name === "AbortError" ||
          error?.code === "ERR_CANCELED"
        ) {
          throw error;
        }

        if (retryCount >= maxRetries || isCancelled) {
          throw error;
        }

        await sleep(retryWaitTime);
        return fetchWithRetry(index, retryCount + 1);
      }
    };

    const fetchResultPage = async (index) => {
      const response = await fetchWithRetry(index);
      const responseData = response?.data;
      const newData = Array.isArray(responseData)
        ? responseData
        : responseData?.results || [];

      if (newData.length === 0) {
        return 0;
      }

      const { eph, vendor, notFound } = mapApiResponseToRecords(
        newData,
        fetchedRows,
      );
      fetchedRows += newData.length;

      if (eph.length > 0) {
        setEphRecordsData((prev) => [...prev, ...eph]);
        hasReceivedData = true;
      }

      if (vendor.length > 0) {
        setVendorRecordsData((prev) => [...prev, ...vendor]);
        hasReceivedData = true;
      }

      if (notFound.length > 0) {
        setNotFoundRecordsData((prev) => [...prev, ...notFound]);
        hasReceivedData = true;
      }

      if (hasReceivedData) {
        setIsLoading(false);
      }

      return newData.length;
    };

    const fetchAdvanceResults = async () => {
      let currentIndex = 0;
      let shouldContinue = true;

      setIsLoading(true);
      setContinuePolling(true);
      setPollingTimeRemaining(maxPollingTime);
      setProcessingProgress({ processed: 0, total: 0 });
      setSummaryBreakdown(DEFAULT_SUMMARY_BREAKDOWN);
      setEphRecordsData([]);
      setNotFoundRecordsData([]);
      setVendorRecordsData([]);
      setEphSelected(new Set());
      setNotFoundSelected(new Set());
      setVendorSelected(new Set());
      setKeptRecords([]);

      while (shouldContinue && !isCancelled) {
        if (Date.now() - startTime >= maxPollingTime) {
          break;
        }

        try {
          const summaryResponse = await fetchSummaryWithRetry();
          const summaryData = summaryResponse?.data || {};
          const searchStatus = String(summaryData?.search_status)
            .trim()
            .toUpperCase();

          if (searchStatus === "REVIEW_SUBMITTED") {
            setContinuePolling(false);
            setIsLoading(false);
            history.push("/upload-history");
            return;
          }

          const totalRecords = Number(summaryData?.total_records || 0);
          const recordsProcessed = Number(
            summaryData?.total_records_completed || 0,
          );
          const ephTotal = Number(summaryData?.eph_records || 0);
          const ephCompleted = Number(summaryData?.eph_search_completed || 0);
          const vendorTotal = Number(summaryData?.vendor_search_records || 0);
          const vendorCompleted = Number(
            summaryData?.vendor_search_completed || 0,
          );
          const notFoundTotal = Number(summaryData?.not_found || 0);

          setProcessingProgress({
            processed: recordsProcessed,
            total: totalRecords,
          });
          setSummaryBreakdown({
            ephTotal,
            ephCompleted,
            vendorTotal,
            vendorCompleted,
            notFoundTotal,
            notFoundCompleted: notFoundTotal,
          });

          if (recordsProcessed === 0) {
            if (!updatePollingTime()) {
              shouldContinue = false;
              setContinuePolling(false);
              break;
            }

            await sleep(STATUS_API_POLL_INTERVAL_MS);
            continue;
          }

          const availableToFetch = Math.max(0, recordsProcessed - fetchedRows);

          if (availableToFetch > 0) {
            const pagesToFetch =
              availableToFetch > BULK_FETCH_BATCH_THRESHOLD
                ? BULK_FETCH_BATCH_PAGES
                : Math.min(
                    BULK_FETCH_BATCH_PAGES,
                    Math.ceil(availableToFetch / BULK_FETCH_PAGE_SIZE),
                  );

            for (
              let pageOffset = 0;
              pageOffset < pagesToFetch;
              pageOffset += 1
            ) {
              const fetchedCount = await fetchResultPage(currentIndex);
              currentIndex += 1;

              if (fetchedCount === 0 || isCancelled) {
                break;
              }
            }
          }

          const isProcessingComplete =
            totalRecords > 0 && recordsProcessed >= totalRecords;
          const hasFetchedAllProcessedRows = fetchedRows >= recordsProcessed;

          if (isProcessingComplete && hasFetchedAllProcessedRows) {
            shouldContinue = false;
            setContinuePolling(false);
            break;
          }

          if (!updatePollingTime()) {
            shouldContinue = false;
            setContinuePolling(false);
            break;
          }

          await sleep(STATUS_API_POLL_INTERVAL_MS);
        } catch (error) {
          if (
            signal.aborted ||
            error?.name === "AbortError" ||
            error?.code === "ERR_CANCELED"
          ) {
            // request cancelled during unmount or navigation
          } else {
            console.error("Bulk upload advance results fetch failed.", error);
          }
          shouldContinue = false;
          setContinuePolling(false);
        }
      }

      if (!hasReceivedData) {
        setIsLoading(false);
      }
      setContinuePolling(false);
    };

    fetchAdvanceResults();

    return () => {
      isCancelled = true;
      controller.abort();
      setIsLoading(false);
      setContinuePolling(false);
      resumePollingTriggeredRef.current.clear();
    };
  }, [history, uploadId]);

  // After records finish loading, resume polling for any vendor-searched entities
  // whose status is not yet COMPLETED. Skip entities whose data is already present.
  useEffect(() => {
    if (continuePolling) {
      return;
    }

    const allRecords = [
      ...ephRecordsData,
      ...vendorRecordsData,
      ...notFoundRecordsData,
    ];

    allRecords.forEach((record) => {
      const status = record?.vendorSearchStatus || {};
      const pendingTypes = [];

      if (
        status?.opted_for_hcp_vendor_search &&
        String(status?.hcp_vendor_search_status || "").toUpperCase() !==
          "COMPLETED"
      ) {
        pendingTypes.push("hcp");
      }
      if (
        status?.opted_for_hco_vendor_search &&
        String(status?.hco_vendor_search_status || "").toUpperCase() !==
          "COMPLETED"
      ) {
        pendingTypes.push("hco");
      }
      if (
        status?.opted_for_parent_hco_vendor_search &&
        String(status?.parent_hco_vendor_search_status || "").toUpperCase() !==
          "COMPLETED"
      ) {
        pendingTypes.push("parent_hco");
      }

      if (pendingTypes.length > 0) {
        resumeVendorPolling(record, pendingTypes);
      }
    });
  }, [continuePolling]);

  const hasEntityInputForPayload = (entity) =>
    !!entity && typeof entity === "object" && Object.keys(entity).length > 0;

  const normalizeSelectedEntityPayload = (item, fallbackEntityType) => {
    if (!item) {
      return null;
    }

    const entityId = item.entityId || item.id || "";
    const isVendorSource =
      String(item?.sourceType || "")
        .trim()
        .toLowerCase() === "vendor";

    return {
      ...(isVendorSource ? { onekeyid: entityId } : { EntityId: entityId }),
      type: item.type || fallbackEntityType || "",
      HCPUniqueId: item.enterpriseId || "",
      HCPReferenceId: item.referenceId || item.id || "",
      ActiveFlag: item.activeFlag || "",
      CountryCode: item.country || "",
      ClassificationCode: item.classificationCode || "",
      Name: item.name || "",
      FirstName: item.firstName || "",
      LastName: item.lastName || "",
      Specialities: item.primarySpecialty ? [item.primarySpecialty] : [],
    };
  };

  const buildSelectedEntityFullPayload = (item) => {
    if (!item) {
      return null;
    }

    return item?.backendPayload ? { ...item.backendPayload } : { ...item };
  };

  const getSelectedSource = (record, entityKey, selectedRecord) => {
    if (!selectedRecord) {
      return "";
    }

    const vendorKey =
      entityKey === "hcp"
        ? "hcp_vendor"
        : entityKey === "hco"
          ? "hco_vendor"
          : "parent_hco_vendor";

    // Use vendor backend key only when selected result is from vendor source.
    const selectedSourceType = String(selectedRecord?.sourceType || "")
      .trim()
      .toLowerCase();

    if (selectedSourceType === "vendor") {
      const backendVendorSource = String(record?.[vendorKey] || "").trim();
      return backendVendorSource || "vendor";
    }

    // For EPH or any other source, use the record's source field.
    const rawSource =
      entityKey === "hcp"
        ? record?.hcpSource
        : entityKey === "hco"
          ? record?.hcoSource
          : record?.parentHcoSource;
    return String(rawSource || "").trim();
  };

  const buildSelectionEntry = (
    record,
    selectedSet,
    accordionSelectedMap,
    tabId,
  ) => {
    const isReviewed = selectedSet.has(record.id);

    const selectedHcpId = accordionSelectedMap?.[`${record.id}-hcp`];
    const selectedHcoId = accordionSelectedMap?.[`${record.id}-hco`];
    const selectedParentHcoId =
      accordionSelectedMap?.[`${record.id}-parentHco`];

    const findSelectedRecord = (entityKey, selectedId) => {
      if (!selectedId) {
        return null;
      }

      const candidates = [
        ...(record?.accordionData?.[entityKey] || []),
        ...(record?.ephAccordionData?.[entityKey] || []),
        ...(record?.vendorAccordionData?.[entityKey] || []),
      ];

      return candidates.find((item) => item.id === selectedId) || null;
    };

    const selectedHcpRecord = findSelectedRecord("hcp", selectedHcpId);
    const selectedHcoRecord = findSelectedRecord("hco", selectedHcoId);
    const selectedParentHcoRecord = findSelectedRecord(
      "parentHco",
      selectedParentHcoId,
    );

    // Determine layout configuration
    const inputEntities = record?.inputEntities || {};
    const hasInputHcp = hasEntityInputForPayload(inputEntities.hcp);
    const hasInputHco = hasEntityInputForPayload(inputEntities.hco);
    const hasInputParentHco = hasEntityInputForPayload(inputEntities.parentHco);
    const isSingleHcpLayout = hasInputHcp && !hasInputHco && !hasInputParentHco;
    const isSingleHcoLayout = hasInputHco && !hasInputHcp && !hasInputParentHco;
    const isHcoParentLayout = hasInputHco && hasInputParentHco && !hasInputHcp;
    const isHcpParentLayout = hasInputHcp && hasInputParentHco && !hasInputHco;

    const useHcoOnLeft = isSingleHcoLayout || isHcoParentLayout;
    const showRightTab = !(isSingleHcpLayout || isSingleHcoLayout);
    const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;

    const leftTabData = useHcoOnLeft
      ? record?.accordionData?.hco
      : record?.accordionData?.hcp;
    const rightTabData = useParentHcoOnRight
      ? record?.accordionData?.parentHco
      : record?.accordionData?.hco;
    const hcpData = record?.accordionData?.hcp || [];
    const hcoData = record?.accordionData?.hco || [];
    const parentHcoData = record?.accordionData?.parentHco || [];

    const leftEntityType = useHcoOnLeft ? "hco" : "hcp";
    const rightEntityType = useParentHcoOnRight ? "parent_hco" : "hco";

    // Determine if this is a unique-result record (checkbox-only, no radio selection needed).
    // Mirrors isUniqueResultRecord logic used to enable the checkbox.
    const leftCount = useHcoOnLeft ? hcoData.length : hcpData.length;
    const rightCount = useParentHcoOnRight
      ? parentHcoData.length
      : hcoData.length;
    const isUniqueRecord = showRightTab
      ? leftCount === 1 && rightCount === 1
      : leftCount === 1;

    // Auto-pick the single result ONLY for unique-result records when checkbox is checked.
    // For multi-result records, only explicit radio selections count.
    const autoPickForUniqueRecord = (explicitRecord, data) => {
      if (explicitRecord) return explicitRecord;
      if (!isReviewed || !isUniqueRecord) return null;
      if (!Array.isArray(data) || data.length !== 1) return null;
      return data[0];
    };

    const effectiveHcpRecord = autoPickForUniqueRecord(
      selectedHcpRecord,
      hcpData,
    );
    const effectiveHcoRecord = autoPickForUniqueRecord(
      selectedHcoRecord,
      hcoData,
    );
    const effectiveParentHcoRecord = autoPickForUniqueRecord(
      selectedParentHcoRecord,
      parentHcoData,
    );

    const selectedResult = {};
    const selectedResultFullData = {};
    if (effectiveHcpRecord) {
      selectedResult.hcp = normalizeSelectedEntityPayload(
        effectiveHcpRecord,
        "HCP",
      );
      selectedResultFullData.hcp =
        buildSelectedEntityFullPayload(effectiveHcpRecord);
    }
    if (effectiveHcoRecord) {
      selectedResult.hco = normalizeSelectedEntityPayload(
        effectiveHcoRecord,
        "HCO",
      );
      selectedResultFullData.hco =
        buildSelectedEntityFullPayload(effectiveHcoRecord);
    }
    if (effectiveParentHcoRecord) {
      selectedResult.parent_hco = normalizeSelectedEntityPayload(
        effectiveParentHcoRecord,
        "PARENT_HCO",
      );
      selectedResultFullData.parent_hco = buildSelectedEntityFullPayload(
        effectiveParentHcoRecord,
      );
    }

    const isHcpSelected = !!effectiveHcpRecord;
    const isHcoSelected = !!effectiveHcoRecord;
    const isParentHcoSelected = !!effectiveParentHcoRecord;

    const payload = {
      status: isReviewed ? "REVIEWED" : "NOT_REVIEWED",
      pk: record?.pk || "",
      // selected_result:
      //   Object.keys(selectedResult).length > 0 ? selectedResult : false,
      selected_result:
        Object.keys(selectedResultFullData).length > 0
          ? selectedResultFullData
          : false,
    };

    if (hasInputHcp) {
      payload.is_hcp_selected = isHcpSelected;
      payload.hcp_selected_source = getSelectedSource(
        record,
        "hcp",
        effectiveHcpRecord,
      );
    }

    if (hasInputHco) {
      payload.is_hco_selected = isHcoSelected;
      payload.hco_selected_source = getSelectedSource(
        record,
        "hco",
        effectiveHcoRecord,
      );
    }

    if (hasInputParentHco) {
      payload.is_parent_hco_selected = isParentHcoSelected;
      payload.parent_hco_selected_source = getSelectedSource(
        record,
        "parent_hco",
        effectiveParentHcoRecord,
      );
    }

    if (tabId === "notfound") {
      payload.is_flagged_as_not_found = isReviewed;
    }

    return payload;
  };

  const buildReviewPayload = () => {
    const keptEphIds = new Set(
      keptRecords.filter((r) => r.id.startsWith("eph-")).map((r) => r.id),
    );
    const keptVendorIds = new Set(
      keptRecords.filter((r) => r.id.startsWith("ven-")).map((r) => r.id),
    );
    const keptNotFoundIds = new Set(
      keptRecords.filter((r) => r.id.startsWith("nf-")).map((r) => r.id),
    );

    const ephEntries = ephRecordsData.map((record) =>
      buildSelectionEntry(record, keptEphIds, ephAccordionSelected, "eph"),
    );
    const vendorEntries = vendorRecordsData.map((record) =>
      buildSelectionEntry(
        record,
        keptVendorIds,
        vendorAccordionSelected,
        "vendor",
      ),
    );
    const notFoundEntries = notFoundRecordsData.map((record) =>
      buildSelectionEntry(
        record,
        keptNotFoundIds,
        notFoundAccordionSelected,
        "notfound",
      ),
    );

    return {
      upload_id: uploadId || "",
      records: [...ephEntries, ...vendorEntries, ...notFoundEntries].filter(
        (entry) => entry?.status === "REVIEWED",
      ),
    };
  };

  const handleSubmit = () => {
    const payload = buildReviewPayload();
    setShowReviewModal(true);
  };

  const getPayloadByteSize = (payload) => {
    const text = JSON.stringify(payload || {});
    return new TextEncoder().encode(text).length;
  };

  const splitPayloadIntoBatches = (payload, maxBytes) => {
    const uploadIdValue = payload?.upload_id || "";
    const records = Array.isArray(payload?.records) ? payload.records : [];
    const basePayload = { upload_id: uploadIdValue, records: [] };
    const baseSize = getPayloadByteSize(basePayload);

    if (baseSize > maxBytes) {
      throw new Error("Base payload exceeds allowed batch size.");
    }

    const batches = [];
    let currentBatch = [];
    let currentSize = baseSize;

    for (const record of records) {
      const recordSize = getPayloadByteSize(record);

      if (baseSize + recordSize > maxBytes) {
        throw new Error("A single record exceeds 4MB payload size limit.");
      }

      if (currentSize + recordSize > maxBytes && currentBatch.length > 0) {
        batches.push({ upload_id: uploadIdValue, records: currentBatch });
        currentBatch = [record];
        currentSize = baseSize + recordSize;
      } else {
        currentBatch.push(record);
        currentSize += recordSize;
      }
    }

    if (currentBatch.length > 0 || records.length === 0) {
      batches.push({ upload_id: uploadIdValue, records: currentBatch });
    }

    return batches;
  };

  const submitReviewPayload = async (payload) => {
    const MAX_PAYLOAD_BYTES = 4 * 1024 * 1024;
    const totalSize = getPayloadByteSize(payload);
    if (totalSize <= MAX_PAYLOAD_BYTES) {
      await Api.batchSearchResult(payload);
    } else {
      const batchedPayloads = splitPayloadIntoBatches(
        payload,
        MAX_PAYLOAD_BYTES,
      );
      for (const batchPayload of batchedPayloads) {
        await Api.batchSearchResult(batchPayload);
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 5000));

    // Trigger batch result after processing results have been accepted.
    await Api.triggerBatchResult({ upload_id: uploadId });
  };

  const handleFinalSubmit = async () => {
    const payload = buildReviewPayload();

    try {
      await submitReviewPayload(payload);
    } catch (error) {
      console.error("Failed to submit bulk upload review payload.", error);
      throw error;
    }

    const ephSubmitted = keptRecords.filter((r) => r.id.startsWith("eph-"));
    const vendorSubmitted = keptRecords.filter((r) => r.id.startsWith("ven-"));
    const notFoundSubmitted = keptRecords.filter((r) => r.id.startsWith("nf-"));
    const ephData = ephSubmitted.length > 0 ? ephSubmitted : ephRecordsData;
    const vendorData =
      vendorSubmitted.length > 0 ? vendorSubmitted : vendorRecordsData;
    const notFoundData =
      notFoundSubmitted.length > 0 ? notFoundSubmitted : notFoundRecordsData;

    setSubmittedEphRecords(ephData);
    setSubmittedVendorRecords(vendorData);
    setSubmittedNotFoundRecords(notFoundData);
    history.push(`/bulk-upload/results`);
  };

  const escapeCsvValue = (value) => {
    const normalizedValue = value == null ? "" : String(value);
    if (
      normalizedValue.includes(",") ||
      normalizedValue.includes('"') ||
      normalizedValue.includes("\n")
    ) {
      return `"${normalizedValue.replace(/"/g, '""')}"`;
    }
    return normalizedValue;
  };

  const getFirstValue = (...values) =>
    values.find(
      (value) => value !== undefined && value !== null && value !== "",
    );

  const toTitleCaseHeader = (value) =>
    String(value || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const buildExportRows = (records, tabLabel) => {
    const entityConfigs = [
      { key: "hcp", label: "HCP", sourceField: "hcpSource" },
      { key: "hco", label: "HCO", sourceField: "hcoSource" },
      {
        key: "parentHco",
        label: "Parent HCO",
        sourceField: "parentHcoSource",
      },
    ];

    return records.flatMap((record) => {
      const accordionMatches = entityConfigs.flatMap(
        ({ key, label, sourceField }) => {
          const items = Array.isArray(record?.accordionData?.[key])
            ? record.accordionData[key]
            : [];

          return items.map((item) => ({
            tab: tabLabel,
            enterpriseId: item?.enterpriseId || record?.enterpriseId || "",
            name: item?.name || record?.name || "",
            country: item?.country || record?.country || "",
            classificationCode:
              item?.classificationCode || record?.classificationCode || "",
            activeFlag: item?.activeFlag || record?.activeFlag || "",
            address: item?.address || record?.address || "",
            entityId:
              item?.entityId ||
              item?.id ||
              record?.entityId ||
              record?.id ||
              "",
            source:
              item?.source || record?.[sourceField] || record?.status || "",
            entityType: item?.entityType || item?.type || label,
          }));
        },
      );

      if (accordionMatches.length > 0) {
        return accordionMatches;
      }

      return {
        tab: tabLabel,
        enterpriseId: record?.enterpriseId || "",
        name: record?.name || "",
        country: record?.country || "",
        classificationCode: record?.classificationCode || "",
        activeFlag: record?.activeFlag || "",
        address: record?.address || "",
        entityId: record?.entityId || record?.id || "",
        source:
          record?.source ||
          record?.hcpSource ||
          record?.hcoSource ||
          record?.parentHcoSource ||
          record?.status ||
          "",
        entityType: record?.entityType || "",
      };
    });
  };

  const buildInputEntityExportDataset = (records, tabLabel, entityKey) => {
    const excludedResultHeaders = new Set([
      "enterprise id",
      "country",
      "status",
      "primary specialty",
      "source type",
      "phone",
      "phone number",
    ]);
    const hcoOnlyExcludedResultHeaders =
      entityKey === "hco" ? new Set(["first name", "last name"]) : new Set();

    const validEntries = records
      .map((record) => {
        const input = record?.inputEntities?.[entityKey];
        const resultItems = Array.isArray(record?.accordionData?.[entityKey])
          ? record.accordionData[entityKey]
          : [];

        if (
          !input ||
          typeof input !== "object" ||
          Object.keys(input).length === 0
        ) {
          return null;
        }

        return {
          rowId: record?.id || "",
          input,
          resultItems,
        };
      })
      .filter(Boolean);

    const inputFieldKeys = [];
    const inputFieldKeySet = new Set();

    validEntries.forEach(({ input }) => {
      Object.keys(input).forEach((key) => {
        if (!inputFieldKeySet.has(key)) {
          inputFieldKeySet.add(key);
          inputFieldKeys.push(key);
        }
      });
    });

    const resultFieldKeys = [];
    const resultFieldKeySet = new Set();

    const getExportResultObject = (item) => {
      const backendResult =
        item?.backendPayload && typeof item.backendPayload === "object"
          ? item.backendPayload
          : {};

      return {
        ...backendResult,
        ...item,
        entityId:
          getFirstValue(
            item?.entityId,
            backendResult?.EntityId,
            item?.id,
            "",
          ) || "",
        activeFlag:
          getFirstValue(item?.activeFlag, backendResult?.ActiveFlag, "") || "",
      };
    };

    validEntries.forEach(({ resultItems }) => {
      (resultItems || []).forEach((item) => {
        const exportResult = getExportResultObject(item);
        Object.keys(exportResult || {}).forEach((key) => {
          if (key === "id" || key === "backendPayload") {
            return;
          }
          if (!resultFieldKeySet.has(key)) {
            resultFieldKeySet.add(key);
            resultFieldKeys.push(key);
          }
        });
      });
    });

    const inputColumnsToKeep = inputFieldKeys.filter((key) =>
      validEntries.some(({ input }) => {
        const value = input?.[key];
        return (
          value !== undefined && value !== null && String(value).trim() !== ""
        );
      }),
    );

    const resultColumnsToKeep = resultFieldKeys.filter((key) => {
      const header = toTitleCaseHeader(key).toLowerCase();
      return (
        !excludedResultHeaders.has(header) &&
        !hcoOnlyExcludedResultHeaders.has(header)
      );
    });

    const orderedResultColumnsToKeep = [...resultColumnsToKeep];
    const firstNameIndex = orderedResultColumnsToKeep.findIndex(
      (key) => toTitleCaseHeader(key).toLowerCase() === "first name",
    );
    const middleNameIndex = orderedResultColumnsToKeep.findIndex(
      (key) => toTitleCaseHeader(key).toLowerCase() === "middle name",
    );

    if (firstNameIndex !== -1 && middleNameIndex !== -1) {
      const [middleNameKey] = orderedResultColumnsToKeep.splice(
        middleNameIndex,
        1,
      );
      const adjustedFirstNameIndex = orderedResultColumnsToKeep.findIndex(
        (key) => toTitleCaseHeader(key).toLowerCase() === "first name",
      );
      orderedResultColumnsToKeep.splice(
        adjustedFirstNameIndex + 1,
        0,
        middleNameKey,
      );
    }

    const uniqueInputColumns = [];
    const seenInputHeaders = new Set();
    inputColumnsToKeep.forEach((key) => {
      const header = `Input ${toTitleCaseHeader(key)}`;
      const normalizedHeader = header.toLowerCase();
      if (!seenInputHeaders.has(normalizedHeader)) {
        seenInputHeaders.add(normalizedHeader);
        uniqueInputColumns.push({ key, header });
      }
    });

    const uniqueResultColumns = [];
    const seenResultHeaders = new Set();
    orderedResultColumnsToKeep.forEach((key) => {
      const header = toTitleCaseHeader(key);
      const normalizedHeader = header.toLowerCase();
      if (!seenResultHeaders.has(normalizedHeader)) {
        seenResultHeaders.add(normalizedHeader);
        uniqueResultColumns.push({ key, header });
      }
    });

    const headers = [
      "Row",
      ...uniqueInputColumns.map((column) => column.header),
      ...uniqueResultColumns.map((column) => column.header),
    ];

    const rows = validEntries.flatMap(({ input, resultItems }) => {
      if (!Array.isArray(resultItems) || resultItems.length === 0) {
        return [];
      }

      const uniqueResultItems = [];
      const uniqueResultItemSet = new Set();

      resultItems.forEach((item) => {
        const exportResult = getExportResultObject(item);
        const dedupeKey = JSON.stringify(exportResult || {});
        if (!uniqueResultItemSet.has(dedupeKey)) {
          uniqueResultItemSet.add(dedupeKey);
          uniqueResultItems.push(item);
        }
      });

      return uniqueResultItems.map((item) => [
        "",
        ...uniqueInputColumns.map((column) =>
          getFirstValue(input?.[column.key], ""),
        ),
        ...uniqueResultColumns.map((column) => {
          const exportResult = getExportResultObject(item);
          const rawValue = exportResult?.[column.key];
          if (rawValue === undefined || rawValue === null) {
            return "";
          }
          if (typeof rawValue === "object") {
            try {
              return JSON.stringify(rawValue);
            } catch (error) {
              return String(rawValue);
            }
          }
          return rawValue;
        }),
      ]);
    });

    const uniqueRows = [];
    const uniqueRowSet = new Set();

    rows.forEach((row) => {
      const dedupeKey = JSON.stringify(row.slice(1));
      if (!uniqueRowSet.has(dedupeKey)) {
        uniqueRowSet.add(dedupeKey);
        uniqueRows.push(row);
      }
    });

    const numberedRows = uniqueRows.map((row, index) => {
      const nextRow = [...row];
      nextRow[0] = index + 1;
      return nextRow;
    });

    return { headers, rows: numberedRows };
  };

  const buildNotFoundEntityInputExportDataset = (
    records,
    entityKey,
    entityLabel,
  ) => {
    const validRecords = (records || []).filter((record) => {
      const entityInput = record?.inputEntities?.[entityKey];
      return (
        entityInput &&
        typeof entityInput === "object" &&
        Object.keys(entityInput).length > 0
      );
    });

    const inputFieldKeys = [];
    const inputFieldKeySet = new Set();

    validRecords.forEach((record) => {
      const entityInput = record?.inputEntities?.[entityKey] || {};
      Object.keys(entityInput).forEach((fieldKey) => {
        if (!inputFieldKeySet.has(fieldKey)) {
          inputFieldKeySet.add(fieldKey);
          inputFieldKeys.push(fieldKey);
        }
      });
    });

    const columnsToKeep = inputFieldKeys.filter((fieldKey) =>
      validRecords.some((record) => {
        const value = record?.inputEntities?.[entityKey]?.[fieldKey];
        return (
          value !== undefined && value !== null && String(value).trim() !== ""
        );
      }),
    );

    const headers = [
      "Row",
      ...columnsToKeep.map(
        (fieldKey) => `Input ${entityLabel} ${toTitleCaseHeader(fieldKey)}`,
      ),
    ];

    const rows = validRecords.map((record, index) => [
      index + 1,
      ...columnsToKeep.map((fieldKey) => {
        const rawValue = record?.inputEntities?.[entityKey]?.[fieldKey];
        if (rawValue === undefined || rawValue === null) {
          return "";
        }
        if (typeof rawValue === "object") {
          try {
            return JSON.stringify(rawValue);
          } catch (error) {
            return String(rawValue);
          }
        }
        return rawValue;
      }),
    ]);

    return { headers, rows };
  };

  const triggerCsvDownload = (headers, rows, fileName) => {
    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const utf8Bom = "\uFEFF";

    const blob = new Blob([utf8Bom, csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportAll = () => {
    if (isExportDisabled) {
      return;
    }

    const activeTabConfig =
      activeTab === "eph"
        ? {
            records: ephRecordsData,
            label: "EPH",
            fileName: "eph_results.csv",
          }
        : activeTab === "vendor"
          ? {
              records: vendorRecordsData,
              label: "Vendor",
              fileName: "vendor_results.csv",
            }
          : {
              records: notFoundRecordsData,
              label: "Not Found",
              fileName: "not_found_results.csv",
            };

    if (activeTab === "eph" || activeTab === "vendor") {
      const hcpDataset = buildInputEntityExportDataset(
        activeTabConfig.records,
        activeTabConfig.label,
        "hcp",
      );
      const hcoDataset = buildInputEntityExportDataset(
        activeTabConfig.records,
        activeTabConfig.label,
        "hco",
      );
      const parentHcoDataset = buildInputEntityExportDataset(
        activeTabConfig.records,
        activeTabConfig.label,
        "parentHco",
      );

      const tabPrefix = activeTab === "eph" ? "eph" : "vendor";
      const filesToDownload = [
        {
          dataset: hcpDataset,
          fileName: `${tabPrefix}_hcp_input_results.csv`,
        },
        {
          dataset: hcoDataset,
          fileName: `${tabPrefix}_hco_input_results.csv`,
        },
        {
          dataset: parentHcoDataset,
          fileName: `${tabPrefix}_parent_hco_input_results.csv`,
        },
      ].filter(
        ({ dataset }) =>
          Array.isArray(dataset?.rows) && dataset.rows.length > 0,
      );

      if (filesToDownload.length === 0) {
        const allRecords = buildExportRows(
          activeTabConfig.records,
          activeTabConfig.label,
        );
        const fallbackHeaders = [
          "Tab",
          "Name",
          "Classification Code",
          "Active Flag",
          "Address",
          "EntityId",
        ];
        const fallbackRows = allRecords.map((record) => [
          record.tab,
          record.name,
          record.classificationCode,
          record.activeFlag,
          record.address,
          record.entityId,
        ]);
        triggerCsvDownload(
          fallbackHeaders,
          fallbackRows,
          activeTabConfig.fileName,
        );
        return;
      }

      filesToDownload.forEach(({ dataset, fileName }) => {
        triggerCsvDownload(dataset.headers, dataset.rows, fileName);
      });

      return;
    }

    if (activeTab === "notfound") {
      const hcpDataset = buildNotFoundEntityInputExportDataset(
        activeTabConfig.records,
        "hcp",
        "HCP",
      );
      const hcoDataset = buildNotFoundEntityInputExportDataset(
        activeTabConfig.records,
        "hco",
        "HCO",
      );
      const parentHcoDataset = buildNotFoundEntityInputExportDataset(
        activeTabConfig.records,
        "parentHco",
        "Parent HCO",
      );

      const filesToDownload = [
        {
          dataset: hcpDataset,
          fileName: "not_found_hcp_input_results.csv",
        },
        {
          dataset: hcoDataset,
          fileName: "not_found_hco_input_results.csv",
        },
        {
          dataset: parentHcoDataset,
          fileName: "not_found_parent_hco_input_results.csv",
        },
      ].filter(
        ({ dataset }) =>
          Array.isArray(dataset?.rows) && dataset.rows.length > 0,
      );

      if (filesToDownload.length === 0) {
        const allRecords = buildExportRows(
          activeTabConfig.records,
          activeTabConfig.label,
        );
        const fallbackHeaders = [
          "Tab",
          "Name",
          "Classification Code",
          "Active Flag",
          "Address",
          "EntityId",
        ];
        const fallbackRows = allRecords.map((record) => [
          record.tab,
          record.name,
          record.classificationCode,
          record.activeFlag,
          record.address,
          record.entityId,
        ]);
        triggerCsvDownload(
          fallbackHeaders,
          fallbackRows,
          activeTabConfig.fileName,
        );
        return;
      }

      filesToDownload.forEach(({ dataset, fileName }) => {
        triggerCsvDownload(dataset.headers, dataset.rows, fileName);
      });

      return;
    }

    const allRecords = buildExportRows(
      activeTabConfig.records,
      activeTabConfig.label,
    );

    const fallbackHeaders = [
      "Tab",
      "Name",
      "Classification Code",
      "Active Flag",
      "Address",
      "EntityId",
    ];

    const fallbackRows = allRecords.map((record) => [
      record.tab,
      record.name,
      record.classificationCode,
      record.activeFlag,
      record.address,
      record.entityId,
    ]);

    triggerCsvDownload(fallbackHeaders, fallbackRows, activeTabConfig.fileName);
  };

  useEffect(() => {
    const ephSelectedRecords = ephRecordsData.filter((record) =>
      ephSelected.has(record.id),
    );
    const vendorSelectedRecords = vendorRecordsData.filter((record) =>
      vendorSelected.has(record.id),
    );
    const notFoundSelectedRecords = notFoundRecordsData.filter((record) =>
      notFoundSelected.has(record.id),
    );

    setKeptRecords([
      ...ephSelectedRecords,
      ...vendorSelectedRecords,
      ...notFoundSelectedRecords,
    ]);
  }, [
    ephRecordsData,
    vendorRecordsData,
    notFoundRecordsData,
    ephSelected,
    vendorSelected,
    notFoundSelected,
  ]);

  const handleMoveRecord = (recordId, fromTab, toTab) => {
    let record = null;

    // Find and remove record from source tab
    if (fromTab === "eph") {
      record = ephRecordsData.find((r) => r.id === recordId);
      setEphRecordsData(ephRecordsData.filter((r) => r.id !== recordId));
      setEphSelected(new Set([...ephSelected].filter((id) => id !== recordId)));
    } else if (fromTab === "vendor") {
      record = vendorRecordsData.find((r) => r.id === recordId);
      setVendorRecordsData(vendorRecordsData.filter((r) => r.id !== recordId));
      setVendorSelected(
        new Set([...vendorSelected].filter((id) => id !== recordId)),
      );
    } else if (fromTab === "notfound") {
      record = notFoundRecordsData.find((r) => r.id === recordId);
      setNotFoundRecordsData(
        notFoundRecordsData.filter((r) => r.id !== recordId),
      );
      setNotFoundSelected(
        new Set([...notFoundSelected].filter((id) => id !== recordId)),
      );
    }

    if (!record) return;

    // Generate new ID based on target tab
    const newId =
      toTab === "eph"
        ? `eph-${Date.now()}`
        : toTab === "vendor"
          ? `ven-${Date.now()}`
          : `nf-${Date.now()}`;

    const movedRecord = { ...record, id: newId };

    // Add to target tab at the top
    if (toTab === "eph") {
      setEphRecordsData([movedRecord, ...ephRecordsData]);
    } else if (toTab === "vendor") {
      setVendorRecordsData([movedRecord, ...vendorRecordsData]);
    } else if (toTab === "notfound") {
      setNotFoundRecordsData([movedRecord, ...notFoundRecordsData]);
    }
  };

  const anyLocked = ephLocked || vendorLocked || notFoundLocked;

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === "notfound") {
      setNotFoundPage(1);
    }
    setVisitedTabs((prev) => new Set([...prev, tabId]));
  };

  const filterRecordsBySearch = (records = []) => {
    const query = tableSearchQuery.trim().toLowerCase();
    if (!query) {
      return records;
    }

    const queryTokens = tokenizeSearchValue(query);

    return records.filter((record) => {
      const recordTokens = buildLeftSideSearchTokens(record);

      if (recordTokens.has(query)) {
        return true;
      }

      if (queryTokens.length === 0) {
        return false;
      }

      const searchableTokens = Array.from(recordTokens);
      return queryTokens.every((token) =>
        searchableTokens.some((searchableToken) =>
          searchableToken.startsWith(token),
        ),
      );
    });
  };

  const hasEntityInputForSelection = (entity) =>
    !!entity && typeof entity === "object" && Object.keys(entity).length > 0;

  const isUniqueResultRecord = (record) => {
    const inputEntities = record?.inputEntities || {};
    const hasInputHcp = hasEntityInputForSelection(inputEntities.hcp);
    const hasInputHco = hasEntityInputForSelection(inputEntities.hco);
    const hasInputParentHco = hasEntityInputForSelection(
      inputEntities.parentHco,
    );

    const hcpResultCount = record?.accordionData?.hcp?.length || 0;
    const hcoResultCount = record?.accordionData?.hco?.length || 0;
    const parentHcoResultCount = record?.accordionData?.parentHco?.length || 0;

    const isSingleHcpLayout = hasInputHcp && !hasInputHco && !hasInputParentHco;
    const isSingleHcoLayout = hasInputHco && !hasInputHcp && !hasInputParentHco;
    const isHcoParentLayout = hasInputHco && hasInputParentHco && !hasInputHcp;
    const isHcpParentLayout = hasInputHcp && hasInputParentHco && !hasInputHco;

    const useHcoOnLeft = isSingleHcoLayout || isHcoParentLayout;
    const showRightTabForLayout = !(isSingleHcpLayout || isSingleHcoLayout);
    const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;

    const leftRenderedCount = useHcoOnLeft ? hcoResultCount : hcpResultCount;
    const rightRenderedCount = useParentHcoOnRight
      ? parentHcoResultCount
      : hcoResultCount;

    return showRightTabForLayout
      ? leftRenderedCount === 1 && rightRenderedCount === 1
      : leftRenderedCount === 1;
  };

  const isAutoSelectionEligibleRecord = (record) => {
    const autoSelections = getAutoAccordionSelectionsForRecord(record);
    return !!autoSelections && Object.keys(autoSelections).length > 0;
  };

  const getAutoAccordionSelectionsForRecord = (record) => {
    if (!record) {
      return null;
    }

    const inputEntities = record?.inputEntities || {};
    const hasInputHcp = hasEntityInputForSelection(inputEntities.hcp);
    const hasInputHco = hasEntityInputForSelection(inputEntities.hco);
    const hasInputParentHco = hasEntityInputForSelection(
      inputEntities.parentHco,
    );
    const isSingleHcpLayout = hasInputHcp && !hasInputHco && !hasInputParentHco;
    const isSingleHcoLayout = hasInputHco && !hasInputHcp && !hasInputParentHco;
    const isHcoParentLayout = hasInputHco && hasInputParentHco && !hasInputHcp;
    const isHcpParentLayout = hasInputHcp && hasInputParentHco && !hasInputHco;

    const useHcoOnLeft = isSingleHcoLayout || isHcoParentLayout;
    const showRightTabForLayout = !(isSingleHcpLayout || isSingleHcoLayout);
    const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;

    const leftSelectionType = useHcoOnLeft ? "hco" : "hcp";
    const rightSelectionType = useParentHcoOnRight ? "parentHco" : "hco";
    const leftData = useHcoOnLeft
      ? record?.accordionData?.hco || []
      : record?.accordionData?.hcp || [];
    const rightData = useParentHcoOnRight
      ? record?.accordionData?.parentHco || []
      : record?.accordionData?.hco || [];

    const nextSelections = {};
    if (leftData.length === 1 && leftData[0]?.id) {
      nextSelections[`${record.id}-${leftSelectionType}`] = leftData[0].id;
    }
    if (showRightTabForLayout && rightData.length === 1 && rightData[0]?.id) {
      nextSelections[`${record.id}-${rightSelectionType}`] = rightData[0].id;
    }

    return Object.keys(nextSelections).length > 0 ? nextSelections : null;
  };

  const isRecordAutoSelected = (record, accordionSelectedMap) => {
    const autoSelections = getAutoAccordionSelectionsForRecord(record);
    const autoKeys = Object.keys(autoSelections || {}).filter(
      (key) => autoSelections[key],
    );

    if (autoKeys.length === 0) {
      return false;
    }

    const currentKeys = ["hcp", "hco", "parentHco"]
      .map((type) => `${record.id}-${type}`)
      .filter((key) => !!accordionSelectedMap?.[key]);

    if (currentKeys.length !== autoKeys.length) {
      return false;
    }

    return autoKeys.every(
      (key) => accordionSelectedMap?.[key] === autoSelections[key],
    );
  };

  const clearTabSelections = ({
    records,
    selectedSet,
    setSelectedSet,
    accordionSelectedMap,
    setAccordionSelectedMap,
    mode,
  }) => {
    const getReviewedIdsToClear = () => {
      const selectedIds = [...selectedSet];
      if (selectedIds.length === 0) {
        return [];
      }

      const recordById = new Map(
        (records || []).map((record) => [record.id, record]),
      );
      return selectedIds.filter((id) => {
        const record = recordById.get(id);
        if (!record) {
          return true;
        }
        return !isRecordAutoSelected(record, accordionSelectedMap);
      });
    };

    if (mode === "all") {
      setSelectedSet(new Set());
      setAccordionSelectedMap({});
      return;
    }

    const selectedIds = [...selectedSet];
    const reviewedIdsToClear = getReviewedIdsToClear();

    if (reviewedIdsToClear.length === 0) {
      return;
    }

    const clearSet = new Set(reviewedIdsToClear);
    setSelectedSet(new Set(selectedIds.filter((id) => !clearSet.has(id))));

    const nextAccordionSelected = { ...accordionSelectedMap };
    reviewedIdsToClear.forEach((id) => {
      delete nextAccordionSelected[`${id}-hcp`];
      delete nextAccordionSelected[`${id}-hco`];
      delete nextAccordionSelected[`${id}-parentHco`];
    });
    setAccordionSelectedMap(nextAccordionSelected);
  };

  const handleClearSelections = (mode) => {
    const activeTabConfig =
      activeTab === "eph"
        ? {
            id: "eph",
            records: ephRecordsData,
            selectedSet: ephSelected,
            setSelectedSet: setEphSelected,
            accordionSelectedMap: ephAccordionSelected,
            setAccordionSelectedMap: setEphAccordionSelected,
          }
        : activeTab === "vendor"
          ? {
              id: "vendor",
              records: vendorRecordsData,
              selectedSet: vendorSelected,
              setSelectedSet: setVendorSelected,
              accordionSelectedMap: vendorAccordionSelected,
              setAccordionSelectedMap: setVendorAccordionSelected,
            }
          : {
              id: "notfound",
              records: notFoundRecordsData,
              selectedSet: notFoundSelected,
              setSelectedSet: setNotFoundSelected,
              accordionSelectedMap: notFoundAccordionSelected,
              setAccordionSelectedMap: setNotFoundAccordionSelected,
            };

    clearTabSelections({
      records: activeTabConfig.records,
      selectedSet: activeTabConfig.selectedSet,
      setSelectedSet: activeTabConfig.setSelectedSet,
      accordionSelectedMap: activeTabConfig.accordionSelectedMap,
      setAccordionSelectedMap: activeTabConfig.setAccordionSelectedMap,
      mode,
    });

    const requestSeed = `${Date.now()}`;
    setClearSelectionsRequests((prev) => ({
      ...prev,
      [activeTabConfig.id]: {
        id: `${requestSeed}-${activeTabConfig.id}`,
        mode,
      },
    }));
    setHeaderSelectionRequests((prev) => ({
      ...prev,
      [activeTabConfig.id]: null,
    }));
    setHeaderSelectionMode((prev) => ({
      ...prev,
      [activeTabConfig.id]: null,
    }));

    setShowClearSelectionsModal(false);
  };

  const isRecordSelectable = (record, tab) => {
    if (tab.id === "notfound") {
      return true;
    }

    const isLockedAndNotSelected =
      tab.isLocked && !tab.selectedIds.has(record.id);

    const disabledByStatus = record.status === "Not Found";
    return !disabledByStatus && !isLockedAndNotSelected;
  };

  const isVendorCountrySupported = isCountryEligibleForVendorSearch();

  useEffect(() => {
    if (!isVendorCountrySupported && activeTab === "vendor") {
      setActiveTab("eph");
      setVisitedTabs((prev) => {
        const next = new Set(prev);
        next.add("eph");
        return next;
      });
    }
  }, [isVendorCountrySupported, activeTab]);

  // Check if submit should be enabled.
  // Only tabs that currently have records are mandatory to visit.
  const requiredTabIds = [
    {
      id: "eph",
      hasRecords: Array.isArray(ephRecordsData) && ephRecordsData.length > 0,
    },
    {
      id: "vendor",
      hasRecords:
        Array.isArray(vendorRecordsData) && vendorRecordsData.length > 0,
    },
    {
      id: "notfound",
      hasRecords:
        Array.isArray(notFoundRecordsData) && notFoundRecordsData.length > 0,
    },
  ]
    .filter((tab) => (tab.id === "vendor" ? isVendorCountrySupported : true))
    .filter((tab) => tab.hasRecords)
    .map((tab) => tab.id);
  const allTabsVisited = requiredTabIds.every((tabId) =>
    visitedTabs.has(tabId),
  );
  const hasAtLeastOnePayloadSelection = keptRecords.length > 0;
  const isSubmitEnabled =
    !isLoading &&
    !continuePolling &&
    processingProgress.total > 0 &&
    processingProgress.processed >= processingProgress.total &&
    allTabsVisited &&
    hasAtLeastOnePayloadSelection;

  const getSubmitDisableReason = () => {
    if (isLoading) {
      return "Please wait while we load the results...";
    }
    if (continuePolling) {
      return "Processing is in progress. Please wait while all records are processed.";
    }
    if (processingProgress.total === 0) {
      return "No records available to submit.";
    }
    if (processingProgress.processed < processingProgress.total) {
      return `Processing in progress: ${processingProgress.processed} of ${processingProgress.total} records processed.`;
    }
    if (!allTabsVisited) {
      const unvisitedTabs = requiredTabIds.filter(
        (tabId) => !visitedTabs.has(tabId),
      );
      const formatTabName = (tabId) => {
        if (tabId === "eph") return "EPH";
        if (tabId === "vendor") return "Vendor";
        if (tabId === "notfound") return "Not Found";
        return tabId;
      };
      const formattedTabs = unvisitedTabs.map(formatTabName).join(", ");
      const tabLabel = unvisitedTabs.length === 1 ? "tab" : "tabs";
      return `Required review for the ${formattedTabs} ${tabLabel}.`;
    }
    if (!hasAtLeastOnePayloadSelection) {
      return "Please select at least one record to submit.";
    }
    return "";
  };

  const tabs = [
    {
      id: "eph",
      label: "EPH",
      icon: Search,
      records: filterRecordsBySearch(ephRecordsData),
      selectedIds: ephSelected,
      setSelectedIds: setEphSelected,
      isLocked: ephLocked,
      currentPage: ephPage,
      setCurrentPage: setEphPage,
      accordionSelected: ephAccordionSelected,
      setAccordionSelected: setEphAccordionSelected,
      showAccordion: ephShowAccordion,
      setShowAccordion: setEphShowAccordion,
    },
    ...(isVendorCountrySupported
      ? [
          {
            id: "vendor",
            label: "Vendor",
            icon: Business,
            records: filterRecordsBySearch(vendorRecordsData),
            selectedIds: vendorSelected,
            setSelectedIds: setVendorSelected,
            isLocked: vendorLocked,
            currentPage: vendorPage,
            setCurrentPage: setVendorPage,
            accordionSelected: vendorAccordionSelected,
            setAccordionSelected: setVendorAccordionSelected,
            showAccordion: vendorShowAccordion,
            setShowAccordion: setVendorShowAccordion,
          },
        ]
      : []),
    {
      id: "notfound",
      label: "Not found",
      icon: ErrorOutline,
      records: filterRecordsBySearch(notFoundRecordsData),
      selectedIds: notFoundSelected,
      setSelectedIds: setNotFoundSelected,
      isLocked: notFoundLocked,
      currentPage: notFoundPage,
      setCurrentPage: setNotFoundPage,
      accordionSelected: notFoundAccordionSelected,
      setAccordionSelected: setNotFoundAccordionSelected,
      showAccordion: notFoundShowAccordion,
      setShowAccordion: setNotFoundShowAccordion,
    },
  ];

  const currentTab = tabs.find((t) => t.id === activeTab);
  const currentTabSelectableRecordIds = currentTab
    ? currentTab.records
        .filter((record) => isRecordSelectable(record, currentTab))
        .map((record) => record.id)
    : [];
  const currentTabHeaderSelectableRecordIds = currentTab
    ? currentTab.records
        .filter((record) => {
          if (!isRecordSelectable(record, currentTab)) {
            return false;
          }

          if (currentTab.id === "notfound") {
            return true;
          }

          return isAutoSelectionEligibleRecord(record);
        })
        .map((record) => record.id)
    : [];
  const isHeaderCheckboxChecked =
    currentTabHeaderSelectableRecordIds.length > 0 &&
    currentTabHeaderSelectableRecordIds.every((id) =>
      currentTab.selectedIds.has(id),
    );
  const selectedCountInCurrentTab = currentTab
    ? currentTabSelectableRecordIds.filter((id) =>
        currentTab.selectedIds.has(id),
      ).length
    : 0;
  const isAllRecordsSelectedInCurrentTab =
    currentTabSelectableRecordIds.length > 0 &&
    selectedCountInCurrentTab === currentTabSelectableRecordIds.length;
  const isHeaderCheckboxIndeterminate =
    !isAllRecordsSelectedInCurrentTab && selectedCountInCurrentTab > 0;
  const isHeaderCheckboxDisabled = currentTabSelectableRecordIds.length === 0;
  const handleOpenSelectAllMenu = (event) => {
    event.stopPropagation();
    setSelectAllMenuAnchor(event.currentTarget);
  };
  const handleCloseSelectAllMenu = () => setSelectAllMenuAnchor(null);
  const applyHeaderSelection = (recordIds) => {
    if (!currentTab) {
      return;
    }
    const requestSeed = `${Date.now()}`;
    setHeaderSelectionRequests((prev) => ({
      ...prev,
      [currentTab.id]: {
        id: `${requestSeed}-${currentTab.id}`,
        checked: true,
        recordIds,
      },
    }));
  };
  const handleSelectAllAutoSelected = () => {
    applyHeaderSelection(currentTabHeaderSelectableRecordIds);
    if (currentTab) {
      setHeaderSelectionMode((prev) => ({ ...prev, [currentTab.id]: "auto" }));
    }
    handleCloseSelectAllMenu();
  };
  const handleSelectAllRecords = () => {
    applyHeaderSelection(currentTabSelectableRecordIds);
    if (currentTab) {
      setHeaderSelectionMode((prev) => ({ ...prev, [currentTab.id]: "all" }));
    }
    handleCloseSelectAllMenu();
  };
  const handleNotFoundHeaderCheckboxChange = (event) => {
    if (!currentTab) {
      return;
    }
    const checked = !!event.target.checked;
    const requestSeed = `${Date.now()}`;
    setHeaderSelectionRequests((prev) => ({
      ...prev,
      [currentTab.id]: {
        id: `${requestSeed}-${currentTab.id}`,
        checked,
        recordIds: currentTabSelectableRecordIds,
      },
    }));
  };
  const handleConfirmSkipReviewSelectAll = () => {
    // Apply "Select All Records" logic (identical to the dropdown option) to
    // every tab at once.  We set headerSelectionRequests so that each tab's
    // DataTable processes the selection through its normal useEffect path
    // (accordion cleanup, clearedAutoSelectionKeys, etc.).  We also set each
    // tab's selectedIds directly so that background (unmounted) tabs have
    // correct state immediately — their DataTable will reconcile when mounted.
    const allTabConfigs = [
      {
        id: "eph",
        records: ephRecordsData,
        isLocked: ephLocked,
        selectedIds: ephSelected,
        setSelectedIds: setEphSelected,
      },
      ...(isVendorCountrySupported
        ? [
            {
              id: "vendor",
              records: vendorRecordsData,
              isLocked: vendorLocked,
              selectedIds: vendorSelected,
              setSelectedIds: setVendorSelected,
            },
          ]
        : []),
      {
        id: "notfound",
        records: notFoundRecordsData,
        isLocked: notFoundLocked,
        selectedIds: notFoundSelected,
        setSelectedIds: setNotFoundSelected,
      },
    ];

    const nextHeaderModes = {};
    const requestSeed = `${Date.now()}`;
    const nextHeaderRequests = {};

    allTabConfigs.forEach((tabConfig) => {
      const selectableIds = tabConfig.records
        .filter((record) => isRecordSelectable(record, tabConfig))
        .map((record) => record.id);

      if (selectableIds.length > 0) {
        tabConfig.setSelectedIds(new Set(selectableIds));
        nextHeaderModes[tabConfig.id] = "all";
        // Mirror applyHeaderSelection per tab so DataTable handles accordion
        // state, clearedAutoSelectionKeys, etc. — same as "Select All Records".
        nextHeaderRequests[tabConfig.id] = {
          id: `${requestSeed}-${tabConfig.id}`,
          checked: true,
          recordIds: selectableIds,
        };
      }
    });

    setHeaderSelectionRequests((prev) => ({ ...prev, ...nextHeaderRequests }));
    setHeaderSelectionMode((prev) => ({ ...prev, ...nextHeaderModes }));
    setVisitedTabs((prev) => {
      const next = new Set(prev);
      allTabConfigs.forEach((tabConfig) => next.add(tabConfig.id));
      return next;
    });
    setShowSkipReviewModal(false);
    // Open Review modal directly to reduce user clicks
    setShowReviewModal(true);
  };

  // Keep a tab fully selected as more records stream in after "select all"
  // was applied (records can still be appending while polling finishes).
  useEffect(() => {
    if (headerSelectionMode.notfound !== "all") {
      return;
    }
    const allIds = notFoundRecordsData.map((record) => record.id);
    setNotFoundSelected((prev) => {
      const hasAllIds = allIds.every((id) => prev.has(id));
      return hasAllIds && prev.size === allIds.length ? prev : new Set(allIds);
    });
  }, [notFoundRecordsData, headerSelectionMode.notfound]);

  useEffect(() => {
    if (headerSelectionMode.eph !== "all") {
      return;
    }
    const allIds = ephRecordsData
      .filter((record) =>
        isRecordSelectable(record, {
          id: "eph",
          isLocked: ephLocked,
          selectedIds: ephSelected,
        }),
      )
      .map((record) => record.id);
    setEphSelected((prev) => {
      const hasAllIds = allIds.every((id) => prev.has(id));
      return hasAllIds && prev.size === allIds.length ? prev : new Set(allIds);
    });
  }, [ephRecordsData, headerSelectionMode.eph]);

  useEffect(() => {
    if (headerSelectionMode.vendor !== "all" || !isVendorCountrySupported) {
      return;
    }
    const allIds = vendorRecordsData
      .filter((record) =>
        isRecordSelectable(record, {
          id: "vendor",
          isLocked: vendorLocked,
          selectedIds: vendorSelected,
        }),
      )
      .map((record) => record.id);
    setVendorSelected((prev) => {
      const hasAllIds = allIds.every((id) => prev.has(id));
      return hasAllIds && prev.size === allIds.length ? prev : new Set(allIds);
    });
  }, [vendorRecordsData, headerSelectionMode.vendor, isVendorCountrySupported]);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = isHeaderCheckboxIndeterminate;
    }
  }, [isHeaderCheckboxIndeterminate]);
  const hasCurrentTabData =
    currentTab &&
    Array.isArray(currentTab.records) &&
    currentTab.records.length > 0;

  const hasExportableDataForTab = (tabId) => {
    if (tabId === "eph" || tabId === "vendor") {
      const tabRecords = tabId === "eph" ? ephRecordsData : vendorRecordsData;
      return tabRecords.some((record) => {
        const hasHcpDataset =
          hasEntityInputForPayload(record?.inputEntities?.hcp) &&
          (record?.accordionData?.hcp?.length || 0) > 0;
        const hasHcoDataset =
          hasEntityInputForPayload(record?.inputEntities?.hco) &&
          (record?.accordionData?.hco?.length || 0) > 0;
        const hasParentHcoDataset =
          hasEntityInputForPayload(record?.inputEntities?.parentHco) &&
          (record?.accordionData?.parentHco?.length || 0) > 0;
        return hasHcpDataset || hasHcoDataset || hasParentHcoDataset;
      });
    }

    if (tabId === "notfound") {
      return notFoundRecordsData.some((record) => {
        const hasHcpInput = hasEntityInputForPayload(
          record?.inputEntities?.hcp,
        );
        const hasHcoInput = hasEntityInputForPayload(
          record?.inputEntities?.hco,
        );
        const hasParentHcoInput = hasEntityInputForPayload(
          record?.inputEntities?.parentHco,
        );
        return hasHcpInput || hasHcoInput || hasParentHcoInput;
      });
    }

    return false;
  };

  const isBulkDataCompletelyLoaded =
    !isLoading &&
    !continuePolling &&
    processingProgress.total > 0 &&
    processingProgress.processed >= processingProgress.total;

  // "Skip Review & Select All" should disable once every selectable record
  // across all tabs is already selected, and re-enable as soon as any
  // record gets unselected (manually or via Clear Selections).
  const isEverySelectableRecordAlreadySelected = (() => {
    const tabConfigs = [
      {
        id: "eph",
        records: ephRecordsData,
        isLocked: ephLocked,
        selectedIds: ephSelected,
      },
      ...(isVendorCountrySupported
        ? [
            {
              id: "vendor",
              records: vendorRecordsData,
              isLocked: vendorLocked,
              selectedIds: vendorSelected,
            },
          ]
        : []),
      {
        id: "notfound",
        records: notFoundRecordsData,
        isLocked: notFoundLocked,
        selectedIds: notFoundSelected,
      },
    ];

    return tabConfigs.every((tabConfig) => {
      const selectableIds = tabConfig.records
        .filter((record) => isRecordSelectable(record, tabConfig))
        .map((record) => record.id);

      if (selectableIds.length === 0) {
        return true;
      }

      return selectableIds.every((id) => tabConfig.selectedIds.has(id));
    });
  })();

  const hasActiveTabExportData = hasExportableDataForTab(activeTab);
  const isExportDisabled =
    !isBulkDataCompletelyLoaded || !hasActiveTabExportData;
  const hasAnySelectionsInActiveTab =
    activeTab === "eph"
      ? ephSelected.size > 0
      : activeTab === "vendor"
        ? vendorSelected.size > 0
        : notFoundSelected.size > 0;
  const getReviewedClearCountForActiveTab = () => {
    const tabConfig =
      activeTab === "eph"
        ? {
            records: ephRecordsData,
            selectedSet: ephSelected,
            accordionSelectedMap: ephAccordionSelected,
          }
        : activeTab === "vendor"
          ? {
              records: vendorRecordsData,
              selectedSet: vendorSelected,
              accordionSelectedMap: vendorAccordionSelected,
            }
          : {
              records: notFoundRecordsData,
              selectedSet: notFoundSelected,
              accordionSelectedMap: notFoundAccordionSelected,
            };

    const selectedIds = [...tabConfig.selectedSet];
    if (selectedIds.length === 0) {
      return 0;
    }

    const recordById = new Map(
      (tabConfig.records || []).map((record) => [record.id, record]),
    );
    return selectedIds.filter((id) => {
      const record = recordById.get(id);
      if (!record) {
        return true;
      }
      return !isRecordAutoSelected(record, tabConfig.accordionSelectedMap);
    }).length;
  };

  const activeTabReviewedClearCount = getReviewedClearCountForActiveTab();
  const activeTabSelectionCount =
    activeTab === "eph"
      ? ephSelected.size
      : activeTab === "vendor"
        ? vendorSelected.size
        : notFoundSelected.size;

  const handleCloseClearSelectionsModal = () => {
    setShowClearSelectionsModal(false);
    setClearSelectionMode("reviewed");
  };

  const handleConfirmClearSelections = () => {
    handleClearSelections(clearSelectionMode);
  };

  return (
    <div className="container body-height extra-margin-top-less mb-4 container-border-margen">
      <div>
        <Box sx={{ mb: 3 }}>
          <div>
            <Box
              sx={{
                display: "flex",

                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <BackButton buttonIcon="yes" backUrl="/bulk-upload" />
              <Typography
                variant="h5"
                className="fw-bold _wg-dynamic-text mb-1"
                style={{ width: "70.5%", fontSize: "20px" }}
              >
                Review Console
              </Typography>
              <Chip
                label={`Upload ID: ${uploadId}`}
                sx={{
                  backgroundColor: "#f0f9ff",
                  borderColor: "#0ea5e9",
                  color: "#0c4a6e",
                  fontWeight: 600,
                  border: "2px solid #0ea5e9",
                  fontSize: "0.90rem",
                  height: "auto",
                  padding: "4px 8px",
                  "& .MuiChip-label": {
                    paddingLeft: "6px",
                    paddingRight: "6px",
                  },
                }}
                variant="outlined"
              />
            </Box>
          </div>
        </Box>
        <div className="bulk-ui min-h-screen px-4 bg-background overflow-x-hidden">
          <div className="max-w-full mx-auto">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-1.5 text-sm text-green-800">
                <span className="font-semibold">Country:</span>
                <span>
                  <strong>{userCountry}</strong>
                </span>
                <span className="mx-1 h-4 w-px bg-green-300" />
                <span className="font-medium">
                  {`${processingProgress.processed}/${processingProgress.total} processed`}
                </span>
                <span className="mx-1 h-4 w-px bg-green-300" />
                <span className="font-medium">
                  {`${summaryBreakdown.ephCompleted}/${summaryBreakdown.ephTotal} EPH`}
                </span>
                {isVendorCountrySupported && (
                  <>
                    <span className="mx-1 h-4 w-px bg-green-300" />
                    <span className="font-medium">
                      {`${summaryBreakdown.vendorCompleted}/${summaryBreakdown.vendorTotal} Vendor`}
                    </span>
                  </>
                )}
                <span className="mx-1 h-4 w-px bg-green-300" />
                <span className="font-medium">
                  {`Not Found : ${summaryBreakdown.notFoundTotal}`}
                </span>
              </div>
              <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                <Button
                  variant="outlined"
                  startIcon={<HelpOutlineIcon />}
                  onClick={() => setShowHelpModal(true)}
                  sx={{ textTransform: "none", fontWeight: 500 }}
                  title="View help documentation for this page"
                >
                  Help
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setClearSelectionMode("reviewed");
                    setShowClearSelectionsModal(true);
                  }}
                  disabled={
                    !isBulkDataCompletelyLoaded || !hasAnySelectionsInActiveTab
                  }
                  sx={{ textTransform: "none", fontWeight: 500 }}
                >
                  Clear Selections
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportAll}
                  disabled={isExportDisabled}
                  // className="export-all-data-search"
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    "&.Mui-disabled": {
                      color: "#9ca3af",
                      borderColor: "#d1d5db",
                      backgroundColor: "#f9fafb",
                    },
                  }}
                >
                  Export Results
                </Button>
              </div>
            </div>

            {/* Header with grid layout */}
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-4 mb-4 bg-white border border-border rounded-lg px-4 py-2.5">
              {/* Left section - Checkbox and Tabs */}
              <div className="flex items-center gap-4 h-9">
                <div className="flex items-center gap-1 h-9 w-[54px] flex-shrink-0">
                  {currentTab?.id === "notfound" ? (
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={isHeaderCheckboxChecked}
                      disabled={isHeaderCheckboxDisabled}
                      onChange={handleNotFoundHeaderCheckboxChange}
                      className="w-4 h-4 rounded accent-primary cursor-pointer"
                    />
                  ) : (
                    <>
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        checked={isHeaderCheckboxChecked}
                        disabled={isHeaderCheckboxDisabled}
                        readOnly
                        onClick={(event) => event.preventDefault()}
                        className="w-4 h-4 rounded accent-primary cursor-default"
                      />
                      <IconButton
                        size="small"
                        disabled={isHeaderCheckboxDisabled}
                        onClick={handleOpenSelectAllMenu}
                        aria-label="Select all options"
                        sx={{
                          p: 0.25,
                          color: headerSelectionMode[currentTab?.id]
                            ? "#D31710"
                            : undefined,
                        }}
                      >
                        <ArrowDropDownIcon sx={{ fontSize: "1.25rem" }} />
                      </IconButton>
                      <Menu
                        anchorEl={selectAllMenuAnchor}
                        open={Boolean(selectAllMenuAnchor)}
                        onClose={handleCloseSelectAllMenu}
                      >
                        <MenuItem
                          onClick={handleSelectAllAutoSelected}
                          selected={
                            headerSelectionMode[currentTab?.id] === "auto"
                          }
                          sx={{
                            alignItems: "flex-start",
                            whiteSpace: "normal",
                            py: 1,
                            "&.Mui-selected": {
                              backgroundColor: "rgba(211, 23, 16, 0.12)",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(211, 23, 16, 0.18)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.25,
                              maxWidth: 320,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: "#111827",
                              }}
                            >
                              Select All Auto-Selected Records
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "#6b7280",
                                whiteSpace: "normal",
                              }}
                            >
                              Only records automatically selected by the system
                              based on predefined rules and validations will be
                              processed to create new DCRs.
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem
                          onClick={handleSelectAllRecords}
                          selected={
                            headerSelectionMode[currentTab?.id] === "all"
                          }
                          sx={{
                            alignItems: "flex-start",
                            whiteSpace: "normal",
                            py: 1,
                            "&.Mui-selected": {
                              backgroundColor: "rgba(211, 23, 16, 0.12)",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "rgba(211, 23, 16, 0.18)",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.25,
                              maxWidth: 320,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                fontWeight: 600,
                                color: "#111827",
                              }}
                            >
                              Select All Records
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color: "#b45309",
                                whiteSpace: "normal",
                              }}
                            >
                              ⚠️ Warning: All records will be selected and
                              processed without your review using the supplied
                              information in the uploaded file. This may result
                              in duplicate Profiles.
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </div>
                <div className="flex gap-2 h-9">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`flex items-center gap-2 px-3 h-9 text-sm rounded-md transition-all ${
                          activeTab === tab.id
                            ? "active-tab-cs not-active-tab-cs"
                            : "not-active-tab-cs"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Middle section - Search */}
              <div className="relative max-w-md mx-auto w-full h-9">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search records..."
                  value={tableSearchQuery}
                  onChange={(event) => setTableSearchQuery(event.target.value)}
                  className="w-full h-9 pl-10 pr-4 text-sm bg-gray-50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>

              {/* Right section */}
              <div className="flex items-center justify-end h-9">
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setShowSkipReviewModal(true)}
                  disabled={
                    !isBulkDataCompletelyLoaded ||
                    ephRecordsData.length +
                      vendorRecordsData.length +
                      notFoundRecordsData.length ===
                      0 ||
                    isEverySelectableRecordAlreadySelected
                  }
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    height: 36,
                  }}
                >
                  Skip Review &amp; Select All
                </Button>
              </div>
            </div>
            {continuePolling && pollingTimeRemaining !== null && (
              <Box style={{ marginTop: "10px" }}>
                {/* {processingProgress.total > 0 && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    {`${processingProgress.processed}/${processingProgress.total} processed`}
                  </Typography>
                )} */}
                <LinearProgress
                  variant="indeterminate"
                  style={{
                    color: "rgb(185, 28, 28)",
                  }}
                  color="inherit"
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            {/* Content */}
            {isLoading &&
            ephRecordsData.length === 0 &&
            notFoundRecordsData.length === 0 ? (
              <Box
                sx={{
                  py: 8,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={34} color="error" />
                <Typography variant="body2" color="text.secondary">
                  Please wait while we load the results...
                </Typography>
              </Box>
            ) : null}
            {!isLoading && currentTab && currentTab.records.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 520,
                    border: "1px dashed #D1D5DB",
                    borderRadius: 2,
                    backgroundColor: "#F8FAFC",
                    px: 4,
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <ErrorOutline
                    sx={{
                      fontSize: 34,
                      color: "#9CA3AF",
                      mb: 1,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#374151", mb: 0.5 }}
                  >
                    No result found
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6B7280" }}>
                    No records available in {currentTab.label} tab.
                  </Typography>
                </Box>
              </Box>
            ) : null}
            {hasCurrentTabData && (
              <DataTable
                tabId={currentTab.id}
                records={currentTab.records}
                selectedIds={currentTab.selectedIds}
                onSelectionChange={currentTab.setSelectedIds}
                isLocked={currentTab.isLocked}
                currentPage={currentTab.currentPage}
                setCurrentPage={currentTab.setCurrentPage}
                accordionSelected={currentTab.accordionSelected}
                setAccordionSelected={currentTab.setAccordionSelected}
                showAccordion={currentTab.showAccordion}
                setShowAccordion={currentTab.setShowAccordion}
                onMoveRecord={handleMoveRecord}
                onVendorSearch={handleVendorSearch}
                isCountryEligibleForVendorSearch={isVendorCountrySupported}
                clearSelectionsRequest={clearSelectionsRequests[currentTab.id]}
                headerSelectionRequest={headerSelectionRequests[currentTab.id]}
              />
            )}

            {/* Submit Button */}
          </div>
          <Box
            sx={{ display: "flex", marginTop: "15px", alignItems: "center" }}
          >
            <BackButton buttonIcon="no" backUrl="/bulk-upload" />
            <div
              style={{
                width: "94%",
                textAlign: "right",
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
                alignItems: "center",
              }}
            >
              {!isSubmitEnabled && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <WarningAmberIcon
                    sx={{ fontSize: "1.25rem", color: "#f59e0b" }}
                  />
                  <Typography sx={{ fontSize: "0.8125rem", color: "#92400e" }}>
                    {getSubmitDisableReason()}
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={!isSubmitEnabled}
                sx={{ textTransform: "none", fontWeight: 500, px: 3 }}
                color="error"
              >
                Submit
              </Button>
            </div>
          </Box>

          {/* Review Modal */}
          <ReviewModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            ephTotal={ephRecordsData.length}
            ephSelected={
              keptRecords.filter((r) => r.id.startsWith("eph-")).length
            }
            ephAutoSelected={
              keptRecords.filter(
                (r) => r.id.startsWith("eph-") && isAutoSelectionEligibleRecord(r),
              ).length
            }
            vendorTotal={vendorRecordsData.length}
            vendorSelected={
              keptRecords.filter((r) => r.id.startsWith("ven-")).length
            }
            vendorAutoSelected={
              keptRecords.filter(
                (r) => r.id.startsWith("ven-") && isAutoSelectionEligibleRecord(r),
              ).length
            }
            notFoundTotal={notFoundRecordsData.length}
            notFoundSelected={
              keptRecords.filter((r) => r.id.startsWith("nf-")).length
            }
            isVendorCountrySupported={isVendorCountrySupported}
            onFinalSubmit={handleFinalSubmit}
          />

          <Dialog
            open={showClearSelectionsModal}
            onClose={handleCloseClearSelectionsModal}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "12px",
                maxWidth: "380px",
                width: "100%",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
              },
            }}
          >
            <DialogTitle sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <Typography
                    sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}
                  >
                    Clear Review
                  </Typography>
                </Box>
                <IconButton
                  aria-label="Close clear selections dialog"
                  onClick={handleCloseClearSelectionsModal}
                  size="small"
                  sx={{ color: "#9ca3af", borderRadius: "4px", p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 0 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#000",
                  lineHeight: 1.55,
                  pb: 2.25,
                }}
              >
                Choose what to clear from this tab. This will remove selected
                and checked records based on the option you choose.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Box
                  component="label"
                  onClick={() => setClearSelectionMode("reviewed")}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    border: "1.5px solid",
                    borderColor:
                      clearSelectionMode === "reviewed" ? "#e03636" : "#e5e7eb",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.75,
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                    "&:hover": { borderColor: "#d1d5db" },
                  }}
                >
                  <Box
                    component="input"
                    type="radio"
                    name="clear-selection-mode"
                    checked={clearSelectionMode === "reviewed"}
                    onChange={() => setClearSelectionMode("reviewed")}
                    sx={{
                      appearance: "none",
                      WebkitAppearance: "none",
                      width: 17,
                      height: 17,
                      border: "2px solid #d1d5db",
                      borderRadius: "50%",
                      flexShrink: 0,
                      cursor: "pointer",
                      position: "relative",
                      mt: "1px",
                      transition: "background 0.15s, border-color 0.15s",
                      "&:checked": {
                        borderColor: "#e03636",
                      },
                      "&:checked::after": {
                        content: '""',
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        backgroundColor: "#e03636",
                        transform: "translate(-50%, -50%)",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      Clear Reviewed Only
                    </Typography>
                    <Typography sx={{ fontSize: "12.5px", color: "#9ca3af" }}>
                      {`${activeTabReviewedClearCount} reviewed ${activeTabReviewedClearCount === 1 ? "record" : "records"} will be cleared`}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="label"
                  onClick={() => setClearSelectionMode("all")}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    border: "1.5px solid",
                    borderColor:
                      clearSelectionMode === "all" ? "#e03636" : "#e5e7eb",
                    borderRadius: "8px",
                    px: 2,
                    py: 1.75,
                    cursor: "pointer",
                    transition: "border-color 0.15s",
                    "&:hover": { borderColor: "#d1d5db" },
                  }}
                >
                  <Box
                    component="input"
                    type="radio"
                    name="clear-selection-mode"
                    checked={clearSelectionMode === "all"}
                    onChange={() => setClearSelectionMode("all")}
                    sx={{
                      appearance: "none",
                      WebkitAppearance: "none",
                      width: 17,
                      height: 17,
                      border: "2px solid #d1d5db",
                      borderRadius: "50%",
                      flexShrink: 0,
                      cursor: "pointer",
                      position: "relative",
                      mt: "1px",
                      transition: "background 0.15s, border-color 0.15s",
                      "&:checked": {
                        borderColor: "#e03636",
                      },
                      "&:checked::after": {
                        content: '""',
                        position: "absolute",
                        left: "50%",
                        top: "50%",
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        backgroundColor: "#e03636",
                        transform: "translate(-50%, -50%)",
                      },
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "3px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#111827",
                      }}
                    >
                      Clear All Selections
                    </Typography>
                    <Typography sx={{ fontSize: "12.5px", color: "#9ca3af" }}>
                      Clears both reviewed and auto-selected rows from this tab
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 2.5,
                pb: 2.5,
                pt: 2.5,
                mt: 2.25,
                justifyContent: "flex-end",
                gap: 1.25,
              }}
            >
              <Button
                variant="text"
                color="inherit"
                onClick={handleCloseClearSelectionsModal}
                sx={{
                  textTransform: "none",
                  px: "22px",
                  py: "10px",
                  backgroundColor: "transparent",
                  border: "1.5px solid #d1d5db",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#374151",
                  cursor: "pointer",
                  borderRadius: "8px",
                  transition: "background 0.15s, border-color 0.15s",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmClearSelections}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2.75,
                  py: 1.25,
                  fontSize: "14px",
                  fontWeight: 700,
                  backgroundColor: "#e03636",
                  "&:hover": { backgroundColor: "#c42e2e" },
                }}
              >
                Confirm Clear
              </Button>
            </DialogActions>
          </Dialog>

          {/* Skip Review & Select All Confirmation Modal */}
          <Dialog
            open={showSkipReviewModal}
            onClose={() => setShowSkipReviewModal(false)}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "12px",
                maxWidth: "420px",
                width: "100%",
                overflow: "hidden",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
              },
            }}
          >
            <DialogTitle sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#111827",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <WarningAmberIcon
                    sx={{ fontSize: "1.25rem", color: "#b45309", flexShrink: 0 }}
                  />
                  Caution
                </Typography>
                <IconButton
                  aria-label="Close skip review dialog"
                  onClick={() => setShowSkipReviewModal(false)}
                  size="small"
                  sx={{ color: "#9ca3af", borderRadius: "4px", p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  backgroundColor: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "8px",
                  px: 2,
                  py: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    color: "#92400e",
                    lineHeight: 1.55,
                    textAlign: "justify",
                  }}
                >
                  All records across the EPH, Vendor, and Not Found tabs will be selected for DCR creation and Consents submission without your review, using the supplied information in the uploaded file. This may result in duplicate Profiles. Are you sure you want to continue?
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 2.5,
                pb: 2.5,
                pt: 1,
                justifyContent: "flex-end",
                gap: 1.25,
              }}
            >
              <Button
                variant="text"
                color="inherit"
                onClick={() => setShowSkipReviewModal(false)}
                sx={{
                  textTransform: "none",
                  px: "22px",
                  py: "10px",
                  backgroundColor: "transparent",
                  border: "1.5px solid #d1d5db",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#374151",
                  cursor: "pointer",
                  borderRadius: "8px",
                  transition: "background 0.15s, border-color 0.15s",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                    borderColor: "#9ca3af",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleConfirmSkipReviewSelectAll}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2.75,
                  py: 1.25,
                  fontSize: "14px",
                  fontWeight: 700,
                  backgroundColor: "#e03636",
                  "&:hover": { backgroundColor: "#c42e2e" },
                }}
              >
                Proceed
              </Button>
            </DialogActions>
          </Dialog>

          {/* Help Modal */}
          <Dialog
            open={showHelpModal}
            onClose={() => setShowHelpModal(false)}
            maxWidth="md"
            fullWidth
            sx={{ zIndex: 9500 }}
            PaperProps={{
              sx: {
                borderRadius: "12px",
                maxHeight: "90vh",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.18)",
              },
            }}
          >
            <DialogTitle sx={{ px: 2.5, pt: 2.25, pb: 1.75 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16.5px",
                    fontWeight: 700,
                    color: "#111827",
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    letterSpacing: "-0.01em",
                  }}
                >
                  📋 Review Console Page
                </Typography>
                <IconButton
                  aria-label="Close help dialog"
                  onClick={() => setShowHelpModal(false)}
                  size="small"
                  sx={{ color: "#9ca3af", borderRadius: "4px", p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5, overflow: "auto" }}>
              <Box
                sx={{
                  fontSize: "13.5px",
                  color: "#374151",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  whiteSpace: "normal",
                  wordSpacing: "normal",
                  "& h2": {
                    color: "#1f2937",
                    marginTop: "20px",
                    marginBottom: "10px",
                    fontSize: "14.5px",
                    fontWeight: 600,
                    "&:first-of-type": {
                      marginTop: 0,
                    },
                  },
                  "& h3": {
                    color: "#374151",
                    marginTop: "16px",
                    marginBottom: "8px",
                    fontSize: "13.5px",
                    fontWeight: 600,
                  },
                  "& ul": {
                    listStyle: "none",
                    paddingLeft: 0,
                    marginBottom: "12px",
                  },
                  "& li": {
                    paddingLeft: "22px",
                    marginBottom: "6px",
                    position: "relative",
                    fontSize: "13.5px",
                    lineHeight: 1.5,
                    "&:before": {
                      content: '"→"',
                      position: "absolute",
                      left: 0,
                      color: "#D31710",
                      fontWeight: "bold",
                      fontSize: "22px",
                      fontFamily: "monospace",
                      top: "-8px",
                    },
                  },
                  "& p": {
                    marginBottom: "12px",
                    lineHeight: 1.6,
                    fontSize: "13.5px",
                  },
                  "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "12px",
                    fontSize: "13.5px",
                  },
                  "& th, & td": {
                    border: "1px solid #ddd",
                    padding: "8px 10px",
                    textAlign: "left",
                  },
                  "& th": {
                    backgroundColor: "#f3f4f6",
                    fontWeight: 600,
                  },
                  "& .button-guide": {
                    backgroundColor: "#f0f9ff",
                    borderLeft: "4px solid #0ea5e9",
                    padding: "10px 12px",
                    marginBottom: "10px",
                    borderRadius: "4px",
                    fontSize: "13.5px",
                    lineHeight: 1.5,
                  },
                  "& .button-name": {
                    fontWeight: 600,
                    color: "#D31710",
                  },
                  "& .tab-info": {
                    backgroundColor: "#fef3c7",
                    borderLeft: "4px solid #f59e0b",
                    padding: "10px 12px",
                    marginBottom: "10px",
                    borderRadius: "4px",
                    fontSize: "13.5px",
                    lineHeight: 1.5,
                  },
                  "& .note": {
                    backgroundColor: "#dbeafe",
                    borderLeft: "4px solid #3b82f6",
                    padding: "10px 12px",
                    marginBottom: "10px",
                    borderRadius: "4px",
                    fontSize: "13.5px",
                    lineHeight: 1.5,
                  },
                }}
              >
                <p>
                  Here you can review search results, confirm the correct
                  HCP/HCO matches, request vendor searches, and submit your
                  final selections for DCR and eConsents processing.
                </p>
                <img
                  src={reviewConsoleOverviewImg}
                  alt="Review Console overview"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>🗂️ Understanding the Tabs</h2>
                <p>
                  Results are grouped into tabs based on where a matching
                  profile was found. Review each tab before submitting:
                </p>
                <div className="tab-info">
                  <strong>EPH Tab</strong> - Records that are found in EPH.
                  Note: if even one entity in a record (e.g., HCP,HCO or Parent
                  HCO) is found in EPH, the whole record appears here — the
                  other entity may still come from a Vendor source.
                </div>
                <img
                  src={ephTabImg}
                  alt="EPH tab"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <div className="tab-info">
                  <strong>VENDOR Tab</strong> - Records matched with external
                  vendor sources (IQVIA, OpenData, etc.)
                </div>
                <img
                  src={vendorTabImg}
                  alt="Vendor tab"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <div className="tab-info">
                  <strong>NOT FOUND Tab</strong> - Records with no matching
                  profile found in any system.
                </div>
                <img
                  src={notFoundTabImg}
                  alt="Not Found tab"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>⚡ Auto Selections</h2>
                <p>
                  When a record has exactly one possible HCP, HCO, or Parent HCO
                  match, the system automatically pre-selects it for you. These
                  are called <strong>Auto-Selected Records</strong>.
                </p>
                <ul>
                  <li>
                    Auto-selected records are ticked automatically — no action
                    needed from you unless you want to change the match
                  </li>
                  <li>
                    Records with <strong>multiple</strong> possible matches are
                    <strong> not</strong> auto-selected; you must open them and
                    choose the correct match manually
                  </li>
                  <li>
                    Use the dropdown next to the header checkbox to choose
                    <strong> "Select All Auto-Selected Records"</strong> or
                    <strong> "Select All Records"</strong>
                  </li>
                  <li>
                    <strong>"Select All Records"</strong> also selects
                    multi-match records, leaving their match choice pending
                    review
                  </li>
                  <li>
                    Use the <strong>"Skip Review & Select All"</strong> button
                    to automatically select All Records and send them directly
                    to the next stage
                  </li>
                </ul>
                <img
                  src={headerCheckboxAutoSelectedImg}
                  alt="Header checkbox for auto-selected records"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>✅ Review and Selections</h2>
                <p>
                  Open any record to review its potential HCP/HCO/Parent HCO
                  matches side by side with your uploaded input data.
                </p>
                <ul>
                  <li>Click a record row to open its detail panel</li>
                  <li>
                    Verify the details of the desired result on the right side
                    panel
                  </li>
                  <li>
                    Use the button next to a record to select or deselect it
                    individually
                  </li>
                  <li>
                    You must visit/review every tab at least once before the
                    Submit button becomes available
                  </li>
                </ul>
                <img
                  src={rightSidePanelDetailImg}
                  alt="Right side panel record detail"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>🔎 Vendor Search for EPH Results</h2>
                <p>
                  If a record in the <strong>EPH</strong> tab doesn't have a
                  confident match, you can request a vendor search against
                  external vendor sources (IQVIA, OpenData, etc.) directly using
                  the vendor search button in the record's detail panel.
                </p>
                <ul>
                  <li>
                    Available only for countries eligible for vendor search
                  </li>
                  <li>
                    Select which entity type(s) to search — HCP, HCO, and/or
                    Parent HCO
                  </li>
                  <li>
                    The system fetches the results and updates the record once
                    the vendor search completes
                  </li>
                </ul>
                <img
                  src={onDemandVendorSearchImg}
                  alt="On-demand vendor search"
                  style={{
                    width: "100%",
                    maxWidth: 700,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>🧹 Clear Selections</h2>
                <p>
                  Use the <strong>Clear Selections</strong> button to remove
                  selections and start over for the{" "}
                  <strong>current tab.</strong>
                </p>
                <ul>
                  <li>
                    <strong>Clear reviewed selections</strong> — removes only
                    the selections you manually reviewed/changed, keeping
                    auto-selected records intact
                  </li>
                  <li>
                    <strong>Clear all selections</strong> — removes every
                    selection in the tab, including auto-selected records
                  </li>
                </ul>
                <img
                  src={clearSelectionImg}
                  alt="Clear Selections dialog"
                  style={{
                    width: "100%",
                    maxWidth: 380,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>📤 Results Submission</h2>
                <p>
                  Once you're done reviewing, click <strong>Submit</strong> to
                  send your selected records for processing.
                </p>
                <ul>
                  <li>All tabs must be reviewed before Submit is enabled</li>
                  <li>At least one record must be selected</li>
                  <li>All processing must be complete (no active loading)</li>
                </ul>
                <p>
                  If Submit is disabled, a warning message next to the button
                  explains exactly why.
                </p>
                <img
                  src={reviewSelectionSubmitImg}
                  alt="Review selection and submit"
                  style={{
                    width: "100%",
                    maxWidth: 380,
                    display: "block",
                    margin: "0 auto 16px",
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                  }}
                />

                <h2>📊 Export Data</h2>
                <p>
                  Use the <strong>Export Results</strong> button to download the
                  current review data as an Excel file (.xlsx) for offline
                  review or record-keeping.
                </p>
              </Box>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}